import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Collapser } from "../../components/collapser/Collapser";
import { CloseButton } from "../../components/styled/BasicBlocks";
import { getMindmap, updateMindmap, deleteMindmap, IMindmapObject, IMindmapData, IMindmapArrow, ArrowStyle, SnapPointId, IMindmap } from "../../api/mindmap";
import { uploadMindmapMedia } from "../../api/api";
import { AppContext } from "../../appContext";
import { Spinner } from "../../components/spinner/Spinner";
import { ConfirmationPopup } from "../../components/confirmationPopup/ConfirmationPopup";
import { getFolders } from "../../api/folderApi";
import { IPlanarFolder, makeAnalysisForClient } from "../../pages/dashboardPage/components/fullAnalysis/FSPlanarize";
import { getFolderIconByIndex } from "../../utils/folderIcons";
import { getNotes } from "../../api/notesApi";
import { getCalendarItems } from "../../api/calendarApi";
import { putTaskItem } from "../../api/taskApi";
import { putNote } from "../../api/notesApi";
import { addTaskCheckedListener, removeTaskCheckedListener, triggerTaskCheckedUpdate } from "../../utils/dataSyncTriggers";
import corkboardBg from "../../assets/corkboard.jpg";

interface INote {
    id: string;
    title?: string;
    text?: string;
    clientId?: string;
}

interface ITask {
    id: string;
    title?: string;
    text?: string;
    description?: string;
    clientId?: string;
    client?: string;
    type?: string;
    checked?: boolean;
    hasDate?: boolean;
    Datetime?: number | string;
    datetime?: number | string;
    sessionId?: string;
    tags?: string[];
}
import { 
    getSnapPoints, 
    getSnapPointCoords, 
    getSnapDirection, 
    calculateAutoPathWithSnaps, 
    calculateAutoPath, 
    pathToSvg 
} from "./mindmapArrowUtils";
import "./MindmapEditor.css";

const generateArrowId = () => `arrow_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

interface IMindmapEditorProps {
    mindmapId: string;
    initialName: string;
    onClose: () => void;
    onUpdate?: (item: IMindmap) => void;
    onDelete?: (mindmapId: string) => void;
}

export const MindmapEditor = ({ mindmapId, initialName, onClose, onUpdate, onDelete }: IMindmapEditorProps) => {
    const navigate = useNavigate();
    const [basicCollapsed, setBasicCollapsed] = useState(false);
    const [arrowsCollapsed, setArrowsCollapsed] = useState(false);
    const [otherCollapsed, setOtherCollapsed] = useState(false);
    const [foldersCollapsed, setFoldersCollapsed] = useState(true);
    const [notesCollapsed, setNotesCollapsed] = useState(true);
    const [tasksCollapsed, setTasksCollapsed] = useState(true);
    const [allFolders, setAllFolders] = useState<IPlanarFolder[]>([]);
    const [allNotes, setAllNotes] = useState<INote[]>([]);
    const [allTasks, setAllTasks] = useState<ITask[]>([]);
    const [foldersFilterByClient, setFoldersFilterByClient] = useState(false);
    const [tasksFilterByClient, setTasksFilterByClient] = useState(false);
    const [foldersLoading, setFoldersLoading] = useState(false);
    const [notesLoading, setNotesLoading] = useState(false);
    const [tasksLoading, setTasksLoading] = useState(false);
    const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(new Set());
    const [currentFolderPath, setCurrentFolderPath] = useState<string[]>([]);
    const [objects, setObjects] = useState<IMindmapObject[]>([]);
    const [arrows, setArrows] = useState<IMindmapArrow[]>([]);
    const [name, setName] = useState(initialName);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editingBlockArrowIndex, setEditingBlockArrowIndex] = useState<number | null>(null);
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; index: number } | null>(null);
    const [arrowMode, setArrowMode] = useState<ArrowStyle | null>(null);
    const [arrowStart, setArrowStart] = useState<{ objectIndex: number; snapPoint: SnapPointId } | null>(null);
    const [hoveredObject, setHoveredObject] = useState<number | null>(null);
    const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);
    const [selectedArrowIndex, setSelectedArrowIndex] = useState<number | null>(null);
    const [selectedObjectIndex, setSelectedObjectIndex] = useState<number | null>(null);
    const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
    const [selectionBox, setSelectionBox] = useState<{ startX: number; startY: number; currentX: number; currentY: number } | null>(null);
    const [editingArrowEnd, setEditingArrowEnd] = useState<'start' | 'end' | null>(null);
    const [draggingSegment, setDraggingSegment] = useState<{ segmentIndex: number; startMouse: { x: number; y: number }; origPath: { x: number; y: number }[] } | null>(null);
    const [draggingWaypoint, setDraggingWaypoint] = useState<{ waypointIndex: number; startMouse: { x: number; y: number }; origPath: { x: number; y: number }[] } | null>(null);
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [cameraOffset, setCameraOffset] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [isPanning, setIsPanning] = useState(false);
    const [panStart, setPanStart] = useState<{ x: number; y: number; offsetX: number; offsetY: number } | null>(null);
    const [backgroundColor, setBackgroundColor] = useState('#fff5ec');
    const [showDots, setShowDots] = useState(true);
    const [backgroundType, setBackgroundType] = useState<'color' | 'cork'>('color');
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showMediaModal, setShowMediaModal] = useState(false);
    const [mediaUploadProgress, setMediaUploadProgress] = useState<number | null>(null);
    const [isDrawingMode, setIsDrawingMode] = useState(false);
    const [isTextMode, setIsTextMode] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);
    const [drawingColor, setDrawingColor] = useState('#333333');
    const [drawingWidth, setDrawingWidth] = useState(3);
    const drawingPathRef = useRef<{ x: number; y: number }[]>([]);
    const drawingCanvasRef = useRef<HTMLCanvasElement>(null);
    const drawingRafRef = useRef<number | null>(null);
    const lastDrawPointRef = useRef<{ x: number; y: number } | null>(null);
    const MIN_POINT_DISTANCE = 4;
    const { translate } = useContext(AppContext);
    const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const textareaRefs = useRef<(HTMLDivElement | null)[]>([]);
    const panDistanceRef = useRef<number>(0);
    const objectsRef = useRef<IMindmapObject[]>([]);
    const arrowsRef = useRef<IMindmapArrow[]>([]);
    const nameRef = useRef<string>(initialName);
    const hasPendingChangesRef = useRef<boolean>(false);
    const selectedObjectIndexRef = useRef<number | null>(null);
    const selectedArrowIndexRef = useRef<number | null>(null);
    const selectedIndicesRef = useRef<Set<number>>(new Set());
    const editingIndexRef = useRef<number | null>(null);
    const workspaceRef = useRef<HTMLDivElement>(null);
    const clientIdRef = useRef<string>('');
    const backgroundColorRef = useRef<string>('#fff5ec');
    const showDotsRef = useRef<boolean>(true);
    const backgroundTypeRef = useRef<'color' | 'cork'>('color');
    const historyStackRef = useRef<{ objects: IMindmapObject[]; arrows: IMindmapArrow[] }[]>([]);
    const isUndoingRef = useRef<boolean>(false);
    const HISTORY_LIMIT = 20;

    const pushHistory = useCallback(() => {
        if (isUndoingRef.current) return;
        const snapshot = {
            objects: JSON.parse(JSON.stringify(objectsRef.current)),
            arrows: JSON.parse(JSON.stringify(arrowsRef.current))
        };
        historyStackRef.current = [...historyStackRef.current.slice(-HISTORY_LIMIT + 1), snapshot];
    }, []);

    const undoSaveRef = useRef<((objects: IMindmapObject[], arrows: IMindmapArrow[], name: string) => void) | null>(null);

    const undo = useCallback(() => {
        if (historyStackRef.current.length === 0) return;
        const lastState = historyStackRef.current.pop();
        if (lastState) {
            isUndoingRef.current = true;
            setObjects(lastState.objects);
            objectsRef.current = lastState.objects;
            setArrows(lastState.arrows);
            arrowsRef.current = lastState.arrows;
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
            if (undoSaveRef.current) {
                undoSaveRef.current(lastState.objects, lastState.arrows, nameRef.current);
            }
            setTimeout(() => {
                isUndoingRef.current = false;
            }, 100);
        }
    }, []);

    const simplifyPath = useCallback((points: { x: number; y: number }[], tolerance: number = 2): { x: number; y: number }[] => {
        if (points.length <= 2) return points;
        
        const sqDist = (p1: { x: number; y: number }, p2: { x: number; y: number }) => {
            const dx = p1.x - p2.x;
            const dy = p1.y - p2.y;
            return dx * dx + dy * dy;
        };
        
        const sqDistToSegment = (p: { x: number; y: number }, v: { x: number; y: number }, w: { x: number; y: number }) => {
            const l2 = sqDist(v, w);
            if (l2 === 0) return sqDist(p, v);
            let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
            t = Math.max(0, Math.min(1, t));
            return sqDist(p, { x: v.x + t * (w.x - v.x), y: v.y + t * (w.y - v.y) });
        };
        
        const simplifyDP = (pts: { x: number; y: number }[], first: number, last: number, sqTol: number, simplified: { x: number; y: number }[]) => {
            let maxSqDist = sqTol;
            let index = 0;
            for (let i = first + 1; i < last; i++) {
                const sqD = sqDistToSegment(pts[i], pts[first], pts[last]);
                if (sqD > maxSqDist) {
                    index = i;
                    maxSqDist = sqD;
                }
            }
            if (maxSqDist > sqTol) {
                if (index - first > 1) simplifyDP(pts, first, index, sqTol, simplified);
                simplified.push(pts[index]);
                if (last - index > 1) simplifyDP(pts, index, last, sqTol, simplified);
            }
        };
        
        const sqTolerance = tolerance * tolerance;
        const simplified: { x: number; y: number }[] = [points[0]];
        simplifyDP(points, 0, points.length - 1, sqTolerance, simplified);
        simplified.push(points[points.length - 1]);
        return simplified;
    }, []);

    const redrawCanvas = useCallback(() => {
        const canvas = drawingCanvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        const path = drawingPathRef.current;
        if (path.length < 2) return;
        
        ctx.strokeStyle = drawingColor;
        ctx.lineWidth = drawingWidth * zoom;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        ctx.beginPath();
        const firstPt = path[0];
        const screenFirst = {
            x: firstPt.x * zoom + cameraOffset.x,
            y: firstPt.y * zoom + cameraOffset.y
        };
        ctx.moveTo(screenFirst.x, screenFirst.y);
        
        for (let i = 1; i < path.length; i++) {
            const pt = path[i];
            const screenPt = {
                x: pt.x * zoom + cameraOffset.x,
                y: pt.y * zoom + cameraOffset.y
            };
            ctx.lineTo(screenPt.x, screenPt.y);
        }
        ctx.stroke();
    }, [drawingColor, drawingWidth, zoom, cameraOffset]);

    const loadData = async () => {
        setLoading(true);
        const data = await getMindmap(mindmapId);
        if (data) {
            setName(data.name);
            nameRef.current = data.name;
            clientIdRef.current = data.client_id;
            if (data.json && data.json.objects) {
                setObjects(data.json.objects);
                objectsRef.current = data.json.objects;
            } else {
                setObjects([]);
                objectsRef.current = [];
            }
            if (data.json && data.json.arrows) {
                setArrows(data.json.arrows);
                arrowsRef.current = data.json.arrows;
            } else {
                setArrows([]);
                arrowsRef.current = [];
            }
            if (data.json?.backgroundColor) {
                setBackgroundColor(data.json.backgroundColor);
                backgroundColorRef.current = data.json.backgroundColor;
            }
            if (data.json?.showDots !== undefined) {
                setShowDots(data.json.showDots);
                showDotsRef.current = data.json.showDots;
            }
            if (data.json?.backgroundType) {
                setBackgroundType(data.json.backgroundType);
                backgroundTypeRef.current = data.json.backgroundType;
            }
        }
        hasPendingChangesRef.current = false;
        setLoading(false);
    };

    const initialCenterDoneRef = useRef(false);

    useEffect(() => {
        initialCenterDoneRef.current = false;
        loadData();
    }, [mindmapId]);

    const loadFolders = useCallback(async () => {
        setFoldersLoading(true);
        try {
            const response = await getFolders();
            const data: Array<Record<string, IPlanarFolder>> = await response.json();
            const foldersArray: IPlanarFolder[] = data.map(it => {
                const id = Object.keys(it)[0];
                return {
                    ...it[id],
                    date: new Date(it[id].date),
                    iconIndex: it[id].iconIndex,
                    id
                };
            }).filter(it => it.type === 'folder');
            setAllFolders(foldersArray);
        } catch (e) {
            console.error('Failed to load folders:', e);
        }
        setFoldersLoading(false);
    }, []);

    useEffect(() => {
        loadFolders();
    }, [loadFolders]);

    const loadNotes = useCallback(async () => {
        setNotesLoading(true);
        try {
            const response = await getNotes();
            const data = await response.json();
            if (Array.isArray(data)) {
                const notes: INote[] = data.map((item: any) => {
                    const id = Object.keys(item)[0];
                    return { ...item[id], id };
                });
                setAllNotes(notes);
            }
        } catch (e) {
            console.error('Failed to load notes:', e);
        }
        setNotesLoading(false);
    }, []);

    useEffect(() => {
        loadNotes();
    }, [loadNotes]);

    const loadTasks = useCallback(async () => {
        setTasksLoading(true);
        try {
            const response = await getCalendarItems();
            const data = await response.json();
            if (Array.isArray(data)) {
                const tasks: ITask[] = data
                    .map((item: any) => {
                        const id = Object.keys(item)[0];
                        return { ...item[id], id };
                    })
                    .filter((item: ITask) => item.type === 'task');
                setAllTasks(tasks);
            }
        } catch (e) {
            console.error('Failed to load tasks:', e);
        }
        setTasksLoading(false);
    }, []);

    useEffect(() => {
        loadTasks();
    }, [loadTasks]);

    useEffect(() => {
        const handleTaskCheckedUpdate = (taskId: string, checked: boolean) => {
            setAllTasks(prev => prev.map(t => t.id === taskId ? { ...t, checked } : t));
            const newObjects = objectsRef.current.map(obj => obj.taskId === taskId ? { ...obj, taskChecked: checked } : obj);
            setObjects(newObjects);
            objectsRef.current = newObjects;
        };
        addTaskCheckedListener(handleTaskCheckedUpdate);
        return () => {
            removeTaskCheckedListener(handleTaskCheckedUpdate);
        };
    }, []);

    const toggleTaskChecked = async (task: ITask) => {
        const newChecked = !task.checked;
        setAllTasks(prev => prev.map(t => t.id === task.id ? { ...t, checked: newChecked } : t));
        const newObjects = objectsRef.current.map(obj => obj.taskId === task.id ? { ...obj, taskChecked: newChecked } : obj);
        setObjects(newObjects);
        objectsRef.current = newObjects;
        debouncedSave(newObjects, arrowsRef.current, nameRef.current);
        triggerTaskCheckedUpdate(task.id, newChecked);
        try {
            let datetimeValue: number | undefined = undefined;
            if (task.hasDate) {
                const rawDatetime = task.Datetime || task.datetime;
                if (rawDatetime !== undefined) {
                    if (typeof rawDatetime === 'number') {
                        datetimeValue = rawDatetime;
                    } else if (typeof rawDatetime === 'string') {
                        const parsed = new Date(rawDatetime);
                        if (!isNaN(parsed.getTime())) {
                            datetimeValue = Math.floor(parsed.getTime() / 1000);
                        }
                    }
                }
            }
            const apiPayload = {
                title: task.title || '',
                text: task.text || task.description || '',
                tags: task.tags || [],
                Datetime: datetimeValue,
                hasDate: task.hasDate || false,
                checked: newChecked,
                client: task.client || task.clientId,
                sessionId: task.sessionId,
                type: 'task'
            };
            await putTaskItem(apiPayload as any, task.id!);
        } catch (e) {
            console.error('Failed to update task:', e);
            setAllTasks(prev => prev.map(t => t.id === task.id ? { ...t, checked: !newChecked } : t));
            const revertedObjects = objectsRef.current.map(obj => obj.taskId === task.id ? { ...obj, taskChecked: !newChecked } : obj);
            setObjects(revertedObjects);
            objectsRef.current = revertedObjects;
            debouncedSave(revertedObjects, arrowsRef.current, nameRef.current);
            triggerTaskCheckedUpdate(task.id, !newChecked);
        }
    };

    const syncNoteText = async (noteId: string, newText: string) => {
        const note = allNotes.find(n => n.id === noteId);
        if (!note) return;
        try {
            await putNote({ ...note, text: newText } as any, noteId);
        } catch (e) {
            console.error('Failed to sync note:', e);
        }
    };

    const syncTaskText = async (taskId: string, newText: string) => {
        const task = allTasks.find(t => t.id === taskId);
        if (!task) return;
        try {
            await putTaskItem({ ...task, title: newText } as any, taskId);
        } catch (e) {
            console.error('Failed to sync task:', e);
        }
    };

    const filteredNotes = React.useMemo(() => {
        return allNotes;
    }, [allNotes]);

    const filteredTasks = React.useMemo(() => {
        if (tasksFilterByClient && clientIdRef.current) {
            return allTasks.filter(t => t.clientId === clientIdRef.current);
        }
        return allTasks;
    }, [allTasks, tasksFilterByClient]);

    const filteredFolders = React.useMemo(() => {
        let folders = allFolders.filter(f => f.type === 'folder');
        
        if (foldersFilterByClient && clientIdRef.current) {
            const clientId = clientIdRef.current;
            const allFiles = allFolders.filter(f => f.type === 'file');
            const clientFileParents = new Set<string>();
            
            allFiles.forEach(file => {
                if (file.fileData) {
                    const hasClientData = file.fileData.some((record: any) => record.relatedClientId === clientId);
                    if (hasClientData && file.parent) {
                        clientFileParents.add(file.parent);
                    }
                }
            });
            
            const relevantFolderIds = new Set<string>();
            const addAncestors = (folderId: string) => {
                if (!folderId || relevantFolderIds.has(folderId)) return;
                relevantFolderIds.add(folderId);
                const folder = allFolders.find(f => f.id === folderId);
                if (folder?.parent) {
                    addAncestors(folder.parent);
                }
            };
            
            clientFileParents.forEach(parentId => addAncestors(parentId));
            
            folders = folders.filter(f => relevantFolderIds.has(f.id));
        }
        
        return folders;
    }, [allFolders, foldersFilterByClient]);

    const currentParentId = currentFolderPath.length > 0 ? currentFolderPath[currentFolderPath.length - 1] : null;
    
    const displayedFolders = React.useMemo(() => {
        if (currentParentId === null) {
            return filteredFolders.filter(f => !f.parent);
        }
        return filteredFolders.filter(f => f.parent === currentParentId);
    }, [filteredFolders, currentParentId]);

    const currentFolderName = React.useMemo(() => {
        if (currentParentId === null) return null;
        const folder = allFolders.find(f => f.id === currentParentId);
        return folder?.name || '';
    }, [currentParentId, allFolders]);

    const navigateToFolder = (folderId: string) => {
        setCurrentFolderPath(prev => [...prev, folderId]);
    };

    const navigateBack = () => {
        setCurrentFolderPath(prev => prev.slice(0, -1));
    };

    const hasSubfolders = (folderId: string) => {
        return filteredFolders.some(f => f.parent === folderId);
    };

    const saveData = useCallback(async (currentObjects: IMindmapObject[], currentArrows: IMindmapArrow[], currentName: string, bgColor: string, dots: boolean, bgType: 'color' | 'cork') => {
        setSaving(true);
        const jsonData: IMindmapData = { objects: currentObjects, arrows: currentArrows, backgroundColor: bgColor, showDots: dots, backgroundType: bgType };
        await updateMindmap(mindmapId, currentName, jsonData);
        if (onUpdate) {
            onUpdate({
                id: mindmapId,
                client_id: clientIdRef.current,
                name: currentName,
                json: jsonData
            });
        }
        setSaving(false);
    }, [mindmapId, onUpdate]);

    const debouncedSave = useCallback((currentObjects: IMindmapObject[], currentArrows: IMindmapArrow[], currentName: string, bgColor?: string, dots?: boolean, bgType?: 'color' | 'cork') => {
        objectsRef.current = currentObjects;
        arrowsRef.current = currentArrows;
        nameRef.current = currentName;
        if (bgColor !== undefined) backgroundColorRef.current = bgColor;
        if (dots !== undefined) showDotsRef.current = dots;
        if (bgType !== undefined) backgroundTypeRef.current = bgType;
        hasPendingChangesRef.current = true;
        
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(() => {
            saveData(currentObjects, currentArrows, currentName, backgroundColorRef.current, showDotsRef.current, backgroundTypeRef.current);
            hasPendingChangesRef.current = false;
        }, 2000);
    }, [saveData]);

    useEffect(() => {
        undoSaveRef.current = debouncedSave;
    }, [debouncedSave]);

    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
            if (drawingRafRef.current) {
                cancelAnimationFrame(drawingRafRef.current);
            }
            if (hasPendingChangesRef.current) {
                const jsonData: IMindmapData = { objects: objectsRef.current, arrows: arrowsRef.current, backgroundColor: backgroundColorRef.current, showDots: showDotsRef.current, backgroundType: backgroundTypeRef.current };
                updateMindmap(mindmapId, nameRef.current, jsonData);
                hasPendingChangesRef.current = false;
            }
        };
    }, [mindmapId]);

    const updateObjects = (newObjects: IMindmapObject[], skipHistory = false) => {
        if (!skipHistory && !isUndoingRef.current) pushHistory();
        setObjects(newObjects);
        debouncedSave(newObjects, arrowsRef.current, name);
    };

    useLayoutEffect(() => {
        objects.forEach((obj, i) => {
            const el = textareaRefs.current[i];
            if (!el) return;
            if (i === editingIndex || el === document.activeElement) return;
            const escaped = obj.text
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
            const htmlContent = escaped.replace(/\n/g, '<br>');
            if (el.innerHTML !== htmlContent) {
                el.innerHTML = htmlContent;
            }
        });
    }, [objects, editingIndex]);

    const updateArrows = (newArrows: IMindmapArrow[], skipHistory = false) => {
        if (!skipHistory && !isUndoingRef.current) pushHistory();
        setArrows(newArrows);
        arrowsRef.current = newArrows;
        debouncedSave(objectsRef.current, newArrows, name);
    };

    const addObject = (data: IMindmapObject) => {
        const newObjects = [...objects, data];
        updateObjects(newObjects);
    };

    const handleMarkerSize = (downEvent: React.MouseEvent, setter: (value: IMindmapObject[], moveEvent: MouseEvent) => IMindmapObject[]) => {
        downEvent.stopPropagation();
        pushHistory();
        const moveHandler = (moveEvent: MouseEvent) => {
            setObjects(last => {
                const next = setter(last, moveEvent);
                objectsRef.current = next;
                return next;
            });
        };
        const upHandler = () => {
            window.removeEventListener('mousemove', moveHandler);
            window.removeEventListener('mouseup', upHandler);
            debouncedSave(objectsRef.current, arrowsRef.current, nameRef.current);
        };
        window.addEventListener('mousemove', moveHandler);
        window.addEventListener('mouseup', upHandler);
    };

    const handleClose = async () => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        if (hasPendingChangesRef.current) {
            const jsonData: IMindmapData = { 
                objects: objectsRef.current, 
                arrows: arrowsRef.current,
                backgroundColor: backgroundColorRef.current,
                showDots: showDotsRef.current
            };
            await updateMindmap(mindmapId, nameRef.current, jsonData);
            hasPendingChangesRef.current = false;
            if (onUpdate) {
                onUpdate({
                    id: mindmapId,
                    client_id: clientIdRef.current,
                    name: nameRef.current,
                    json: jsonData
                });
            }
        }
        onClose();
    };

    const handleDeleteClick = () => {
        setShowDeletePopup(true);
    };

    const handleDeleteConfirm = async () => {
        setShowDeletePopup(false);
        await deleteMindmap(mindmapId);
        if (onDelete) {
            onDelete(mindmapId);
        }
        onClose();
    };

    const handleDeleteCancel = () => {
        setShowDeletePopup(false);
    };

    const detectMediaType = (url: string): 'image' | 'video' | 'audio' => {
        const lower = url.toLowerCase();
        if (/\.(mp4|webm|ogg|mov|avi|mkv)(\?|$)/i.test(lower)) return 'video';
        if (/\.(mp3|wav|ogg|aac|flac|m4a)(\?|$)/i.test(lower)) return 'audio';
        return 'image';
    };

    const handleAddMedia = (mediaUrl: string, mediaType: 'image' | 'video' | 'audio') => {
        pushHistory();
        const isAudio = mediaType === 'audio';
        const newObject: IMindmapObject = {
            position: { x: 100 - cameraOffset.x / zoom, y: 100 - cameraOffset.y / zoom },
            width: isAudio ? 300 : 320,
            height: isAudio ? 60 : 240,
            angle: 0,
            text: '',
            style: 'media',
            mediaUrl,
            mediaType
        };
        const newObjects = [...objects, newObject];
        setObjects(newObjects);
        objectsRef.current = newObjects;
        debouncedSave(newObjects, arrowsRef.current, nameRef.current);
        setShowMediaModal(false);
    };

    const handleMediaUrlSubmit = (url: string) => {
        if (!url.trim()) return;
        const mediaType = detectMediaType(url);
        handleAddMedia(url, mediaType);
    };

    const handleMediaFileUpload = async (file: File) => {
        const ext = file.name.split('.').pop()?.toLowerCase() || '';
        let mediaType: 'image' | 'video' | 'audio' = 'image';
        if (['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'].includes(ext)) mediaType = 'video';
        else if (['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a'].includes(ext)) mediaType = 'audio';
        
        setMediaUploadProgress(0);
        try {
            const result = await uploadMindmapMedia(file, `media_${Date.now()}_${file.name}`, (current, total) => {
                setMediaUploadProgress(Math.round((current / total) * 100));
            });
            if (result && result.url) {
                handleAddMedia(result.url, mediaType);
            }
        } catch (error) {
            console.error('Media upload failed:', error);
        } finally {
            setMediaUploadProgress(null);
        }
    };

    const handleExportJson = () => {
        const data = {
            name,
            objects
        };
        const json = JSON.stringify(data, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${name || 'mindmap'}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleDeleteBlock = (index: number) => {
        const newObjects = objects.filter((_, i) => i !== index);
        updateObjects(newObjects);
        setContextMenu(null);
        
        if (arrowStart && arrowStart.objectIndex === index) {
            setArrowStart(null);
            setMousePos(null);
        } else if (arrowStart && arrowStart.objectIndex > index) {
            setArrowStart({ ...arrowStart, objectIndex: arrowStart.objectIndex - 1 });
        }
        
        if (hoveredObject === index) {
            setHoveredObject(null);
        } else if (hoveredObject !== null && hoveredObject > index) {
            setHoveredObject(hoveredObject - 1);
        }
        
        if (selectedArrowIndex !== null) {
            const arrow = arrows[selectedArrowIndex];
            if (arrow && (arrow.fromObjectIndex === index || arrow.toObjectIndex === index)) {
                setSelectedArrowIndex(null);
                setEditingArrowEnd(null);
            }
        }
    };

    const handleContextMenu = (e: React.MouseEvent, index: number) => {
        e.preventDefault();
        if (panDistanceRef.current > 5) {
            return;
        }
        setContextMenu({ x: e.clientX, y: e.clientY, index });
    };

    const closeContextMenu = () => {
        setContextMenu(null);
    };

    useEffect(() => {
        if (!contextMenu) return;
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('.MindmapEditor_contextMenu')) {
                setContextMenu(null);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [contextMenu]);

    const centerCamera = useCallback(() => {
        if (objects.length === 0 || !workspaceRef.current) {
            setCameraOffset({ x: 0, y: 0 });
            setZoom(1);
            return;
        }

        const rect = workspaceRef.current.getBoundingClientRect();
        const viewportWidth = rect.width;
        const viewportHeight = rect.height;

        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        objects.forEach(obj => {
            const halfW = obj.width / 2;
            const halfH = obj.height / 2;
            minX = Math.min(minX, obj.position.x - halfW);
            maxX = Math.max(maxX, obj.position.x + halfW);
            minY = Math.min(minY, obj.position.y - halfH);
            maxY = Math.max(maxY, obj.position.y + halfH);
        });

        const contentWidth = maxX - minX;
        const contentHeight = maxY - minY;
        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        const padding = 50;
        const scaleX = (viewportWidth - padding * 2) / contentWidth;
        const scaleY = (viewportHeight - padding * 2) / contentHeight;
        const newZoom = Math.min(Math.max(Math.min(scaleX, scaleY), 0.1), 4);

        const newOffsetX = viewportWidth / 2 - centerX * newZoom;
        const newOffsetY = viewportHeight / 2 - centerY * newZoom;

        setZoom(newZoom);
        setCameraOffset({ x: newOffsetX, y: newOffsetY });
    }, [objects]);

    useEffect(() => {
        if (!loading && !initialCenterDoneRef.current && workspaceRef.current) {
            requestAnimationFrame(() => {
                centerCamera();
                initialCenterDoneRef.current = true;
            });
        }
    }, [loading, centerCamera]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.code === 'KeyZ' && !e.shiftKey) {
                if (editingIndexRef.current !== null) return;
                const active = document.activeElement;
                const isTextInput = active && (active.tagName === 'TEXTAREA' || active.tagName === 'INPUT' || (active as HTMLElement).isContentEditable);
                if (isTextInput) return;
                e.preventDefault();
                undo();
            }

            if (e.key === 'Delete' || e.key === 'Backspace') {
                if (editingIndexRef.current !== null) return;
                const active = document.activeElement;
                const isTextInput = active && (active.tagName === 'TEXTAREA' || active.tagName === 'INPUT' || (active as HTMLElement).isContentEditable);
                if (isTextInput) return;

                const curSelectedArrow = selectedArrowIndexRef.current;
                if (curSelectedArrow !== null) {
                    e.preventDefault();
                    pushHistory();
                    const newArrows = arrowsRef.current.filter((_, idx) => idx !== curSelectedArrow);
                    setArrows(newArrows);
                    arrowsRef.current = newArrows;
                    debouncedSave(objectsRef.current, newArrows, nameRef.current);
                    setSelectedArrowIndex(null);
                    setEditingArrowEnd(null);
                    return;
                }

                const curSelectedIndices = selectedIndicesRef.current;
                if (curSelectedIndices.size > 0) {
                    e.preventDefault();
                    pushHistory();
                    const indicesToDelete = curSelectedIndices;
                    const newObjects = objectsRef.current.filter((_, i) => !indicesToDelete.has(i));
                    const indexMap = new Map<number, number>();
                    let newIdx = 0;
                    for (let i = 0; i < objectsRef.current.length; i++) {
                        if (!indicesToDelete.has(i)) {
                            indexMap.set(i, newIdx);
                            newIdx++;
                        }
                    }
                    const newArrows = arrowsRef.current
                        .filter(a => !indicesToDelete.has(a.fromObjectIndex) && !indicesToDelete.has(a.toObjectIndex))
                        .map(a => ({
                            ...a,
                            fromObjectIndex: indexMap.get(a.fromObjectIndex)!,
                            toObjectIndex: indexMap.get(a.toObjectIndex)!
                        }));
                    setObjects(newObjects);
                    objectsRef.current = newObjects;
                    setArrows(newArrows);
                    arrowsRef.current = newArrows;
                    debouncedSave(newObjects, newArrows, nameRef.current);
                    setSelectedIndices(new Set());
                    setSelectedObjectIndex(null);
                    setSelectedArrowIndex(null);
                    setEditingArrowEnd(null);
                    setArrowStart(null);
                    setMousePos(null);
                    setHoveredObject(null);
                    setContextMenu(null);
                    return;
                }

                const curSelectedObject = selectedObjectIndexRef.current;
                if (curSelectedObject !== null) {
                    e.preventDefault();
                    pushHistory();
                    const index = curSelectedObject;
                    const newObjects = objectsRef.current.filter((_, i) => i !== index);
                    const newArrows = arrowsRef.current
                        .filter(a => a.fromObjectIndex !== index && a.toObjectIndex !== index)
                        .map(a => ({
                            ...a,
                            fromObjectIndex: a.fromObjectIndex > index ? a.fromObjectIndex - 1 : a.fromObjectIndex,
                            toObjectIndex: a.toObjectIndex > index ? a.toObjectIndex - 1 : a.toObjectIndex
                        }));
                    setObjects(newObjects);
                    objectsRef.current = newObjects;
                    setArrows(newArrows);
                    arrowsRef.current = newArrows;
                    debouncedSave(newObjects, newArrows, nameRef.current);
                    setSelectedObjectIndex(null);
                    setSelectedArrowIndex(null);
                    setEditingArrowEnd(null);
                    setArrowStart(null);
                    setMousePos(null);
                    setHoveredObject(null);
                    setContextMenu(null);
                    return;
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [undo, pushHistory, debouncedSave]);

    const prevWorkspaceSizeRef = useRef<{ width: number; height: number } | null>(null);

    useEffect(() => {
        if (!workspaceRef.current) return;
        
        const updateAfterResize = () => {
            const rect = workspaceRef.current?.getBoundingClientRect();
            if (!rect) return;
            
            const prevSize = prevWorkspaceSizeRef.current;
            if (prevSize) {
                const deltaX = (rect.width - prevSize.width) / 2;
                const deltaY = (rect.height - prevSize.height) / 2;
                setCameraOffset(prev => ({
                    x: prev.x + deltaX,
                    y: prev.y + deltaY
                }));
            }
            prevWorkspaceSizeRef.current = { width: rect.width, height: rect.height };
        };
        
        requestAnimationFrame(() => {
            requestAnimationFrame(updateAfterResize);
        });
    }, [isFullscreen]);

    const zoomRef = useRef(zoom);
    const cameraOffsetRef = useRef(cameraOffset);
    useEffect(() => { zoomRef.current = zoom; }, [zoom]);
    useEffect(() => { cameraOffsetRef.current = cameraOffset; }, [cameraOffset]);
    useEffect(() => { selectedObjectIndexRef.current = selectedObjectIndex; }, [selectedObjectIndex]);
    useEffect(() => { selectedArrowIndexRef.current = selectedArrowIndex; }, [selectedArrowIndex]);
    useEffect(() => { selectedIndicesRef.current = selectedIndices; }, [selectedIndices]);
    useEffect(() => { editingIndexRef.current = editingIndex; }, [editingIndex]);

    useEffect(() => {
        const workspace = workspaceRef.current;
        if (!workspace) return;

        const handleWheel = (e: WheelEvent) => {
            if (!e.ctrlKey) return;
            e.preventDefault();
            
            const rect = workspace.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;

            const currentZoom = zoomRef.current;
            const currentOffset = cameraOffsetRef.current;

            const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;
            const newZoom = Math.min(Math.max(currentZoom * zoomFactor, 0.1), 4);

            const worldX = (mouseX - currentOffset.x) / currentZoom;
            const worldY = (mouseY - currentOffset.y) / currentZoom;

            const newOffsetX = mouseX - worldX * newZoom;
            const newOffsetY = mouseY - worldY * newZoom;

            setZoom(newZoom);
            setCameraOffset({ x: newOffsetX, y: newOffsetY });
        };

        workspace.addEventListener('wheel', handleWheel, { passive: false });
        return () => workspace.removeEventListener('wheel', handleWheel);
    }, [loading]);

    const screenToWorld = useCallback((screenX: number, screenY: number) => {
        return {
            x: (screenX - cameraOffset.x) / zoom,
            y: (screenY - cameraOffset.y) / zoom
        };
    }, [cameraOffset, zoom]);

    if (loading) {
        return <div className="MindmapEditor"><Spinner /></div>;
    }

    return <div className={`MindmapEditor${isFullscreen ? ' MindmapEditor--fullscreen' : ''}`}>
        <div className="MindmapEditor_wrapper">
            <div className="MindmapEditor_tools">
                <div className="MindmapEditor_tools_categories">
                    <div className="MindmapEditor_toolsCollapser_head" onClick={() => setBasicCollapsed(last => !last)}>
                        <div className="MindmapEditor_toolsCollapser_head_title">{translate('mindmap_basic') || 'Basic'}</div>
                        <div className="MindmapEditor_toolsCollapser_head_arrow">^</div>
                    </div>
                    <Collapser className="MindmapEditor_toolsCollapser_collapser" collapsed={!basicCollapsed}>
                        <div className="MindmapEditor_toolsCollapser_items">
                            <div className="MindmapEditor_tool MindmapEditor_tool--box"
                                draggable
                                onDragStart={(e) => {
                                    e.dataTransfer.setData('application/mindmap-shape', JSON.stringify({ style: 'box', width: 100, height: 80 }));
                                    e.dataTransfer.effectAllowed = 'copy';
                                }}
                            ></div>
                            <div className="MindmapEditor_tool MindmapEditor_tool--round" 
                                draggable
                                onDragStart={(e) => {
                                    e.dataTransfer.setData('application/mindmap-shape', JSON.stringify({ style: 'round', width: 100, height: 100 }));
                                    e.dataTransfer.effectAllowed = 'copy';
                                }}
                            ></div>
                            <div className="MindmapEditor_tool MindmapEditor_tool--triangle" 
                                draggable
                                onDragStart={(e) => {
                                    e.dataTransfer.setData('application/mindmap-shape', JSON.stringify({ style: 'triangle', width: 100, height: 90 }));
                                    e.dataTransfer.effectAllowed = 'copy';
                                }}
                            ></div>
                            <div className="MindmapEditor_tool MindmapEditor_tool--trapezoid" 
                                draggable
                                onDragStart={(e) => {
                                    e.dataTransfer.setData('application/mindmap-shape', JSON.stringify({ style: 'trapezoid', width: 120, height: 70 }));
                                    e.dataTransfer.effectAllowed = 'copy';
                                }}
                            ></div>
                            <div className="MindmapEditor_tool MindmapEditor_tool--star" 
                                draggable
                                onDragStart={(e) => {
                                    e.dataTransfer.setData('application/mindmap-shape', JSON.stringify({ style: 'star', width: 100, height: 100 }));
                                    e.dataTransfer.effectAllowed = 'copy';
                                }}
                            ></div>
                            <div className="MindmapEditor_tool MindmapEditor_tool--sticker" 
                                draggable
                                onDragStart={(e) => {
                                    e.dataTransfer.setData('application/mindmap-shape', JSON.stringify({ style: 'sticker', width: 150, height: 100 }));
                                    e.dataTransfer.effectAllowed = 'copy';
                                }}
                            ></div>
                        </div>
                    </Collapser>
                    <div className="MindmapEditor_toolsCollapser_head" onClick={() => setArrowsCollapsed(last => !last)}>
                        <div className="MindmapEditor_toolsCollapser_head_title">{translate('mindmap_arrows') || 'Arrows'}</div>
                        <div className="MindmapEditor_toolsCollapser_head_arrow">^</div>
                    </div>
                    <Collapser className="MindmapEditor_toolsCollapser_collapser" collapsed={!arrowsCollapsed}>
                        <div className="MindmapEditor_arrows_section_label">{translate('mindmap_arrows_manual') || 'Manual'}</div>
                        <div className="MindmapEditor_toolsCollapser_items">
                            <div 
                                className={`MindmapEditor_tool MindmapEditor_tool--arrow-thin ${arrowMode === 'manual-thin' ? 'MindmapEditor_tool--active' : ''}`} 
                                onClick={() => setArrowMode(arrowMode === 'manual-thin' ? null : 'manual-thin')}
                                title={translate('mindmap_arrow_manual_thin') || 'Manual thin'}
                            >
                                <svg width="32" height="32" viewBox="0 0 32 32">
                                    <polyline points="4,20 10,20 10,12 20,12" fill="none" stroke="#f30" strokeWidth="1"/>
                                    <polygon points="24,12 18,8 18,16" fill="#f30"/>
                                    <circle cx="10" cy="16" r="2" fill="#0af"/>
                                </svg>
                            </div>
                            <div 
                                className={`MindmapEditor_tool MindmapEditor_tool--arrow-thick ${arrowMode === 'manual-thick' ? 'MindmapEditor_tool--active' : ''}`} 
                                onClick={() => setArrowMode(arrowMode === 'manual-thick' ? null : 'manual-thick')}
                                title={translate('mindmap_arrow_manual_thick') || 'Manual thick'}
                            >
                                <svg width="32" height="32" viewBox="0 0 32 32">
                                    <polyline points="4,20 10,20 10,12 20,12" fill="none" stroke="#f30" strokeWidth="3"/>
                                    <polygon points="26,12 18,6 18,18" fill="#f30"/>
                                    <circle cx="10" cy="16" r="2.5" fill="#0af"/>
                                </svg>
                            </div>
                            <div 
                                className={`MindmapEditor_tool MindmapEditor_tool--arrow-hollow ${arrowMode === 'manual-hollow' ? 'MindmapEditor_tool--active' : ''}`} 
                                onClick={() => setArrowMode(arrowMode === 'manual-hollow' ? null : 'manual-hollow')}
                                title={translate('mindmap_arrow_manual_hollow') || 'Manual hollow'}
                            >
                                <svg width="32" height="32" viewBox="0 0 32 32">
                                    <polyline points="4,20 10,20 10,12 20,12" fill="none" stroke="#f30" strokeWidth="2"/>
                                    <polygon points="26,12 18,6 18,18" fill="none" stroke="#f30" strokeWidth="1.5"/>
                                    <circle cx="10" cy="16" r="2" fill="#0af"/>
                                </svg>
                            </div>
                        </div>
                        <div className="MindmapEditor_arrows_section_divider"></div>
                        <div className="MindmapEditor_arrows_section_label">{translate('mindmap_arrows_auto') || 'Auto'}</div>
                        <div className="MindmapEditor_toolsCollapser_items">
                            <div 
                                className={`MindmapEditor_tool MindmapEditor_tool--arrow-thin ${arrowMode === 'thin' ? 'MindmapEditor_tool--active' : ''}`} 
                                onClick={() => setArrowMode(arrowMode === 'thin' ? null : 'thin')}
                                title={translate('mindmap_arrow_thin') || 'Thin arrow'}
                            >
                                <svg width="32" height="32" viewBox="0 0 32 32">
                                    <line x1="4" y1="16" x2="24" y2="16" stroke="#f30" strokeWidth="1"/>
                                    <polygon points="28,16 22,12 22,20" fill="#f30"/>
                                </svg>
                            </div>
                            <div 
                                className={`MindmapEditor_tool MindmapEditor_tool--arrow-thick ${arrowMode === 'thick' ? 'MindmapEditor_tool--active' : ''}`} 
                                onClick={() => setArrowMode(arrowMode === 'thick' ? null : 'thick')}
                                title={translate('mindmap_arrow_thick') || 'Thick arrow'}
                            >
                                <svg width="32" height="32" viewBox="0 0 32 32">
                                    <line x1="4" y1="16" x2="24" y2="16" stroke="#f30" strokeWidth="3"/>
                                    <polygon points="28,16 20,10 20,22" fill="#f30"/>
                                </svg>
                            </div>
                            <div 
                                className={`MindmapEditor_tool MindmapEditor_tool--arrow-hollow ${arrowMode === 'hollow' ? 'MindmapEditor_tool--active' : ''}`} 
                                onClick={() => setArrowMode(arrowMode === 'hollow' ? null : 'hollow')}
                                title={translate('mindmap_arrow_hollow') || 'Hollow arrow'}
                            >
                                <svg width="32" height="32" viewBox="0 0 32 32">
                                    <line x1="4" y1="16" x2="24" y2="16" stroke="#f30" strokeWidth="2"/>
                                    <polygon points="28,16 20,10 20,22" fill="none" stroke="#f30" strokeWidth="1.5"/>
                                </svg>
                            </div>
                        </div>
                        <div className="MindmapEditor_arrows_section_divider"></div>
                        <div className="MindmapEditor_arrows_section_label">{translate('mindmap_arrows_straight') || 'Straight'}</div>
                        <div className="MindmapEditor_toolsCollapser_items">
                            <div 
                                className={`MindmapEditor_tool MindmapEditor_tool--arrow-thin ${arrowMode === 'straight-thin' ? 'MindmapEditor_tool--active' : ''}`} 
                                onClick={() => setArrowMode(arrowMode === 'straight-thin' ? null : 'straight-thin')}
                                title={translate('mindmap_arrow_straight_thin') || 'Straight thin'}
                            >
                                <svg width="32" height="32" viewBox="0 0 32 32">
                                    <line x1="4" y1="24" x2="24" y2="8" stroke="#f30" strokeWidth="1"/>
                                    <polygon points="28,5 21,7 23,14" fill="#f30"/>
                                </svg>
                            </div>
                            <div 
                                className={`MindmapEditor_tool MindmapEditor_tool--arrow-thick ${arrowMode === 'straight-thick' ? 'MindmapEditor_tool--active' : ''}`} 
                                onClick={() => setArrowMode(arrowMode === 'straight-thick' ? null : 'straight-thick')}
                                title={translate('mindmap_arrow_straight_thick') || 'Straight thick'}
                            >
                                <svg width="32" height="32" viewBox="0 0 32 32">
                                    <line x1="4" y1="24" x2="24" y2="8" stroke="#f30" strokeWidth="3"/>
                                    <polygon points="28,5 19,6 22,15" fill="#f30"/>
                                </svg>
                            </div>
                            <div 
                                className={`MindmapEditor_tool MindmapEditor_tool--arrow-hollow ${arrowMode === 'straight-hollow' ? 'MindmapEditor_tool--active' : ''}`} 
                                onClick={() => setArrowMode(arrowMode === 'straight-hollow' ? null : 'straight-hollow')}
                                title={translate('mindmap_arrow_straight_hollow') || 'Straight hollow'}
                            >
                                <svg width="32" height="32" viewBox="0 0 32 32">
                                    <line x1="4" y1="24" x2="24" y2="8" stroke="#f30" strokeWidth="2"/>
                                    <polygon points="28,5 19,6 22,15" fill="none" stroke="#f30" strokeWidth="1.5"/>
                                </svg>
                            </div>
                        </div>
                        <div className="MindmapEditor_arrows_section_divider"></div>
                        <div className="MindmapEditor_arrows_section_label">{translate('mindmap_arrows_block') || 'Block'}</div>
                        <div className="MindmapEditor_toolsCollapser_items">
                            <div 
                                className={`MindmapEditor_tool MindmapEditor_tool--arrow-block ${arrowMode === 'block' ? 'MindmapEditor_tool--active' : ''}`} 
                                onClick={() => setArrowMode(arrowMode === 'block' ? null : 'block')}
                                title={translate('mindmap_arrow_block') || 'Block arrow with text'}
                            >
                                <svg width="32" height="32" viewBox="0 0 32 32">
                                    <polygon points="4,11 22,11 22,7 30,16 22,25 22,21 4,21" fill="#f30" />
                                    <text x="13" y="17" fontSize="6" fill="#fff" textAnchor="middle" dominantBaseline="middle">Aa</text>
                                </svg>
                            </div>
                        </div>
                        {arrowMode && (
                            <div className="MindmapEditor_arrow_hint">
                                {translate('mindmap_arrow_hint') || 'Click on a shape to start, then click another shape to connect'}
                            </div>
                        )}
                    </Collapser>
                    <div className="MindmapEditor_toolsCollapser_head" onClick={() => setOtherCollapsed(last => !last)}>
                        <div className="MindmapEditor_toolsCollapser_head_title">{translate('mindmap_other') || 'Other'}</div>
                        <div className="MindmapEditor_toolsCollapser_head_arrow">^</div>
                    </div>
                    <Collapser className="MindmapEditor_toolsCollapser_collapser" collapsed={!otherCollapsed}>
                        <div className="MindmapEditor_toolsCollapser_items">
                            <div 
                                className={`MindmapEditor_tool MindmapEditor_tool--text ${isTextMode ? 'MindmapEditor_tool--active' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsTextMode(!isTextMode);
                                    if (!isTextMode) {
                                        setIsDrawingMode(false);
                                        setArrowMode(null);
                                    }
                                }}
                                title={translate('mindmap_text') || 'Text'}
                            >
                                <span className="MindmapEditor_tool_text_preview">Aa</span>
                            </div>
                            <div 
                                className="MindmapEditor_tool MindmapEditor_tool--media"
                                onClick={() => setShowMediaModal(true)}
                                title={translate('mindmap_media') || 'Media'}
                            >
                                <span className="MindmapEditor_tool_media_icon">▶</span>
                            </div>
                            <div 
                                className={`MindmapEditor_tool MindmapEditor_tool--draw ${isDrawingMode ? 'MindmapEditor_tool--active' : ''}`}
                                onClick={() => {
                                    setIsDrawingMode(!isDrawingMode);
                                    if (isDrawingMode) {
                                        setArrowMode(null);
                                    }
                                }}
                                title={translate('mindmap_draw') || 'Draw'}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 19l7-7 3 3-7 7-3-3z"/>
                                    <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
                                    <path d="M2 2l7.586 7.586"/>
                                    <circle cx="11" cy="11" r="2"/>
                                </svg>
                            </div>
                        </div>
                        {isDrawingMode && (
                            <div className="MindmapEditor_drawing_options">
                                <div className="MindmapEditor_drawing_option_row">
                                    <span className="MindmapEditor_drawing_option_label">{translate('mindmap_draw_color') || 'Color'}</span>
                                    <input 
                                        type="color" 
                                        value={drawingColor}
                                        onChange={(e) => setDrawingColor(e.target.value)}
                                        className="MindmapEditor_drawing_color_input"
                                    />
                                </div>
                                <div className="MindmapEditor_drawing_option_row">
                                    <span className="MindmapEditor_drawing_option_label">{translate('mindmap_draw_width') || 'Width'}</span>
                                    <div className="MindmapEditor_drawing_width_options">
                                        {[2, 3, 5, 8].map(w => (
                                            <button
                                                key={w}
                                                className={`MindmapEditor_drawing_width_btn ${drawingWidth === w ? 'active' : ''}`}
                                                onClick={() => setDrawingWidth(w)}
                                            >
                                                <div style={{ width: w * 3, height: w, backgroundColor: drawingColor, borderRadius: w / 2 }} />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </Collapser>
                    <div className="MindmapEditor_toolsCollapser_head" onClick={() => setFoldersCollapsed(last => !last)}>
                        <div className="MindmapEditor_toolsCollapser_head_title">{translate('mindmap_folders') || 'Folders'}</div>
                        <div 
                            className={`MindmapEditor_filter_icon ${foldersFilterByClient ? '' : 'MindmapEditor_filter_icon--active'}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setFoldersFilterByClient(!foldersFilterByClient);
                            }}
                            title={foldersFilterByClient ? (translate('mindmap_show_all') || 'Show all') : (translate('mindmap_show_client_only') || 'Show client only')}
                        />
                        <div className="MindmapEditor_toolsCollapser_head_arrow">^</div>
                    </div>
                    <Collapser className="MindmapEditor_toolsCollapser_collapser" collapsed={!foldersCollapsed}>
                        {currentFolderPath.length > 0 && (
                            <div className="MindmapEditor_folders_breadcrumb">
                                <button 
                                    className="MindmapEditor_folders_back_btn"
                                    onClick={navigateBack}
                                >
                                    ← {translate('back') || 'Back'}
                                </button>
                                <span className="MindmapEditor_folders_current_name">{currentFolderName}</span>
                            </div>
                        )}
                        <div className="MindmapEditor_folders_list">
                            {foldersLoading && <div className="MindmapEditor_folders_loading"><Spinner /></div>}
                            {!foldersLoading && displayedFolders.length === 0 && (
                                <div className="MindmapEditor_folders_empty">
                                    {currentFolderPath.length > 0 
                                        ? (translate('mindmap_folders_no_subfolders') || 'No subfolders')
                                        : (translate('mindmap_folders_empty') || 'No folders found')
                                    }
                                </div>
                            )}
                            {!foldersLoading && displayedFolders.map(folder => (
                                <div 
                                    key={folder.id}
                                    className="MindmapEditor_folder_item"
                                    draggable
                                    onDragStart={(e) => {
                                        e.dataTransfer.setData('application/mindmap-folder', JSON.stringify({
                                            folderId: folder.id,
                                            folderName: folder.name,
                                            iconIndex: folder.iconIndex || 0
                                        }));
                                        e.dataTransfer.effectAllowed = 'copy';
                                    }}
                                >
                                    <img 
                                        src={getFolderIconByIndex(folder.iconIndex || 0)} 
                                        alt="" 
                                        className="MindmapEditor_folder_item_icon"
                                    />
                                    <span className="MindmapEditor_folder_item_name">{folder.name}</span>
                                    {hasSubfolders(folder.id) && (
                                        <button 
                                            className="MindmapEditor_folder_expand_btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigateToFolder(folder.id);
                                            }}
                                            title={translate('mindmap_folders_open') || 'Open folder'}
                                        >
                                            →
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Collapser>
                    <div className="MindmapEditor_toolsCollapser_head" onClick={() => setNotesCollapsed(last => !last)}>
                        <div className="MindmapEditor_toolsCollapser_head_title">{translate('mindmap_notes') || 'Notes'}</div>
                        <div className="MindmapEditor_toolsCollapser_head_arrow">^</div>
                    </div>
                    <Collapser className="MindmapEditor_toolsCollapser_collapser" collapsed={!notesCollapsed}>
                        <div className="MindmapEditor_notes_list">
                            {notesLoading && <div className="MindmapEditor_notes_loading"><Spinner /></div>}
                            {!notesLoading && filteredNotes.length === 0 && (
                                <div className="MindmapEditor_notes_empty">
                                    {translate('mindmap_notes_empty') || 'No notes found'}
                                </div>
                            )}
                            {!notesLoading && filteredNotes.map(note => (
                                <div 
                                    key={note.id}
                                    className="MindmapEditor_note_item"
                                    draggable
                                    onDragStart={(e) => {
                                        e.dataTransfer.setData('application/mindmap-note', JSON.stringify({
                                            noteId: note.id,
                                            title: note.title || '',
                                            text: note.text || ''
                                        }));
                                        e.dataTransfer.effectAllowed = 'copy';
                                    }}
                                >
                                    <span className="MindmapEditor_note_item_title">{note.title || translate('mindmap_untitled_note') || 'Untitled'}</span>
                                </div>
                            ))}
                        </div>
                    </Collapser>
                    <div className="MindmapEditor_toolsCollapser_head" onClick={() => setTasksCollapsed(last => !last)}>
                        <div className="MindmapEditor_toolsCollapser_head_title">{translate('mindmap_tasks') || 'Tasks'}</div>
                        <div 
                            className={`MindmapEditor_filter_icon ${tasksFilterByClient ? '' : 'MindmapEditor_filter_icon--active'}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setTasksFilterByClient(!tasksFilterByClient);
                            }}
                            title={tasksFilterByClient ? (translate('mindmap_show_all') || 'Show all') : (translate('mindmap_show_client_only') || 'Show client only')}
                        />
                        <div className="MindmapEditor_toolsCollapser_head_arrow">^</div>
                    </div>
                    <Collapser className="MindmapEditor_toolsCollapser_collapser" collapsed={!tasksCollapsed}>
                        <div className="MindmapEditor_tasks_list">
                            {tasksLoading && <div className="MindmapEditor_tasks_loading"><Spinner /></div>}
                            {!tasksLoading && filteredTasks.length === 0 && (
                                <div className="MindmapEditor_tasks_empty">
                                    {translate('mindmap_tasks_empty') || 'No tasks found'}
                                </div>
                            )}
                            {!tasksLoading && filteredTasks.map(task => (
                                <div 
                                    key={task.id}
                                    className={`MindmapEditor_task_item ${task.checked ? 'MindmapEditor_task_item--checked' : ''}`}
                                    draggable
                                    onDragStart={(e) => {
                                        e.dataTransfer.setData('application/mindmap-task', JSON.stringify({
                                            taskId: task.id,
                                            title: task.title || '',
                                            description: task.description || '',
                                            checked: task.checked || false
                                        }));
                                        e.dataTransfer.effectAllowed = 'copy';
                                    }}
                                >
                                    <span 
                                        className={`MindmapEditor_task_item_checkbox ${task.checked ? 'MindmapEditor_task_item_checkbox--checked' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleTaskChecked(task);
                                        }}
                                    ></span>
                                    <span className="MindmapEditor_task_item_title">{task.title || translate('mindmap_untitled_task') || 'Untitled'}</span>
                                </div>
                            ))}
                        </div>
                    </Collapser>
                </div>
                <div className="MindmapEditor_settings">
                    <div className="MindmapEditor_settings_title">{translate('mindmap_settings') || 'Settings'}</div>
                    <div className="MindmapEditor_settings_row">
                        <span className="MindmapEditor_settings_label">{translate('mindmap_background_color') || 'Background'}</span>
                        <div className="MindmapEditor_bg_switcher">
                            <div 
                                className="MindmapEditor_bg_switcher_slider"
                                style={{ transform: backgroundType === 'cork' ? 'translateX(100%)' : 'translateX(0)' }}
                            />
                            <button 
                                className={`MindmapEditor_bg_switcher_btn ${backgroundType === 'color' ? 'active' : ''}`}
                                onClick={() => {
                                    setBackgroundType('color');
                                    backgroundTypeRef.current = 'color';
                                    debouncedSave(objectsRef.current, arrowsRef.current, nameRef.current, backgroundColorRef.current, showDotsRef.current, 'color');
                                }}
                            >
                                <span>{translate('mindmap_background_type_color') || 'Цвет'}</span>
                            </button>
                            <button 
                                className={`MindmapEditor_bg_switcher_btn ${backgroundType === 'cork' ? 'active' : ''}`}
                                onClick={() => {
                                    setBackgroundType('cork');
                                    backgroundTypeRef.current = 'cork';
                                    debouncedSave(objectsRef.current, arrowsRef.current, nameRef.current, backgroundColorRef.current, showDotsRef.current, 'cork');
                                }}
                            >
                                <span>{translate('mindmap_background_type_cork') || 'Доска'}</span>
                            </button>
                        </div>
                    </div>
                    <div className={`MindmapEditor_color_picker_wrapper ${backgroundType === 'color' ? 'visible' : ''}`}>
                        <label className="MindmapEditor_color_label">
                            <input 
                                type="color" 
                                className="MindmapEditor_settings_color_enhanced"
                                value={backgroundColor}
                                onChange={(e) => {
                                    setBackgroundColor(e.target.value);
                                    backgroundColorRef.current = e.target.value;
                                    debouncedSave(objectsRef.current, arrowsRef.current, nameRef.current, e.target.value, showDotsRef.current, backgroundTypeRef.current);
                                }}
                            />
                            <div 
                                className="MindmapEditor_color_preview"
                                style={{ backgroundColor }}
                            />
                        </label>
                        <span className="MindmapEditor_color_hex">{backgroundColor.toUpperCase()}</span>
                    </div>
                    {backgroundType === 'color' && (
                        <div className="MindmapEditor_settings_row">
                            <span className="MindmapEditor_settings_label">{translate('mindmap_show_dots') || 'Show dots'}</span>
                            <label className="MindmapEditor_settings_toggle">
                                <input 
                                    type="checkbox" 
                                    checked={showDots}
                                    onChange={(e) => {
                                        setShowDots(e.target.checked);
                                        showDotsRef.current = e.target.checked;
                                        debouncedSave(objectsRef.current, arrowsRef.current, nameRef.current, backgroundColorRef.current, e.target.checked, backgroundTypeRef.current);
                                    }}
                                />
                                <span className="MindmapEditor_settings_toggle_slider"></span>
                            </label>
                        </div>
                    )}
                </div>
            </div>
            <div className="MindmapEditor_right">
                <div className="MindmapEditor_right_header">
                    <input 
                        className="MindmapEditor_right_header_title_input"
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            debouncedSave(objects, arrows, e.target.value);
                        }}
                    />
                    <CloseButton onClick={handleClose}></CloseButton>
                </div>
                <div 
                    className="MindmapEditor_workspace" 
                    ref={workspaceRef} 
                    tabIndex={0}
                    style={{
                        backgroundColor: backgroundType === 'color' ? backgroundColor : undefined,
                        backgroundImage: backgroundType === 'cork' 
                            ? `url(${corkboardBg})${showDots ? ', radial-gradient(#666 0%, #666 9%, transparent 12%)' : ''}`
                            : (showDots ? 'radial-gradient(#999 0%, #999 9%, transparent 12%)' : 'none'),
                        backgroundSize: backgroundType === 'cork'
                            ? (showDots ? `${2000 * zoom}px auto, ${20 * zoom}px ${20 * zoom}px` : `${2000 * zoom}px auto`)
                            : (showDots ? `${20 * zoom}px ${20 * zoom}px` : undefined),
                        backgroundPosition: backgroundType === 'cork'
                            ? (showDots ? `${cameraOffset.x}px ${cameraOffset.y}px, ${cameraOffset.x}px ${cameraOffset.y}px` : `${cameraOffset.x}px ${cameraOffset.y}px`)
                            : (showDots ? `${cameraOffset.x}px ${cameraOffset.y}px` : undefined),
                        backgroundRepeat: backgroundType === 'cork' ? 'repeat' : undefined,
                        cursor: isPanning ? 'grabbing' : (isDrawingMode ? 'crosshair' : (isTextMode ? 'crosshair' : (arrowMode ? 'crosshair' : 'default')))
                    }}
                    onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'copy';
                    }}
                    onDrop={(e) => {
                        e.preventDefault();
                        const shapeData = e.dataTransfer.getData('application/mindmap-shape');
                        const folderData = e.dataTransfer.getData('application/mindmap-folder');
                        const noteData = e.dataTransfer.getData('application/mindmap-note');
                        const taskData = e.dataTransfer.getData('application/mindmap-task');
                        if (shapeData && workspaceRef.current) {
                            const shape = JSON.parse(shapeData);
                            const rect = workspaceRef.current.getBoundingClientRect();
                            const worldPos = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
                            addObject({
                                position: { x: worldPos.x, y: worldPos.y },
                                width: shape.width,
                                height: shape.height,
                                angle: 0,
                                text: shape.style === 'text' ? (translate('mindmap_text') || 'Text') : "",
                                style: shape.style
                            });
                        } else if (folderData && workspaceRef.current) {
                            const folder = JSON.parse(folderData);
                            const rect = workspaceRef.current.getBoundingClientRect();
                            const worldPos = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
                            addObject({
                                position: { x: worldPos.x, y: worldPos.y },
                                width: 120,
                                height: 100,
                                angle: 0,
                                text: folder.folderName,
                                style: 'folder',
                                folderId: folder.folderId,
                                folderName: folder.folderName
                            });
                        } else if (noteData && workspaceRef.current) {
                            const note = JSON.parse(noteData);
                            const rect = workspaceRef.current.getBoundingClientRect();
                            const worldPos = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
                            addObject({
                                position: { x: worldPos.x, y: worldPos.y },
                                width: 150,
                                height: 100,
                                angle: 0,
                                text: note.title || note.text?.substring(0, 50) || '',
                                style: 'sticker',
                                noteId: note.noteId
                            });
                        } else if (taskData && workspaceRef.current) {
                            const task = JSON.parse(taskData);
                            const rect = workspaceRef.current.getBoundingClientRect();
                            const worldPos = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
                            addObject({
                                position: { x: worldPos.x, y: worldPos.y },
                                width: 150,
                                height: 60,
                                angle: 0,
                                text: task.title || task.description?.substring(0, 50) || '',
                                style: 'box',
                                taskId: task.taskId,
                                taskChecked: task.checked
                            });
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            setSelectedArrowIndex(null);
                            setEditingArrowEnd(null);
                            setSelectedObjectIndex(null);
                        }
                    }}
                    onMouseDown={(e) => {
                        const target = e.target as HTMLElement;
                        const isOnBackground = target === e.currentTarget || target.classList.contains('MindmapEditor_canvas');
                        
                        if (e.button === 2) {
                            closeContextMenu();
                            panDistanceRef.current = 0;
                            setIsPanning(true);
                            setPanStart({ 
                                x: e.clientX, 
                                y: e.clientY, 
                                offsetX: cameraOffset.x, 
                                offsetY: cameraOffset.y 
                            });
                            e.preventDefault();
                        }
                        
                        if (isOnBackground && e.button === 0) {
                            closeContextMenu();
                            if (isTextMode) {
                                const rect = workspaceRef.current?.getBoundingClientRect();
                                if (rect) {
                                    const worldPos = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
                                    const newObject: IMindmapObject = {
                                        position: { x: worldPos.x, y: worldPos.y },
                                        angle: 0,
                                        width: 200,
                                        height: 40,
                                        text: translate('mindmap_text') || 'Text',
                                        style: 'text'
                                    };
                                    const newObjects = [...objects, newObject];
                                    setObjects(newObjects);
                                    objectsRef.current = newObjects;
                                    setSelectedObjectIndex(newObjects.length - 1);
                                    setIsTextMode(false);
                                    debouncedSave(newObjects, arrowsRef.current, name);
                                }
                                return;
                            }
                            if (isDrawingMode) {
                                const rect = workspaceRef.current?.getBoundingClientRect();
                                if (rect) {
                                    const worldPos = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
                                    drawingPathRef.current = [worldPos];
                                    lastDrawPointRef.current = worldPos;
                                    setIsDrawing(true);
                                    if (drawingCanvasRef.current) {
                                        const canvas = drawingCanvasRef.current;
                                        canvas.width = rect.width;
                                        canvas.height = rect.height;
                                    }
                                    redrawCanvas();
                                }
                                return;
                            }
                            if (arrowStart && arrowMode) {
                                setArrowStart(null);
                                setMousePos(null);
                            } else if (!arrowMode) {
                                const rect = workspaceRef.current?.getBoundingClientRect();
                                if (rect) {
                                    const worldPos = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
                                    setSelectionBox({
                                        startX: worldPos.x,
                                        startY: worldPos.y,
                                        currentX: worldPos.x,
                                        currentY: worldPos.y
                                    });
                                }
                            }
                            if (selectedArrowIndex !== null && !editingArrowEnd) {
                                setSelectedArrowIndex(null);
                            }
                            if (editingArrowEnd) {
                                setEditingArrowEnd(null);
                            }
                            setSelectedObjectIndex(null);
                            setSelectedIndices(new Set());
                        }
                    }}
                    onContextMenu={(e) => {
                        const target = e.target as HTMLElement;
                        const isOnBackground = target === e.currentTarget || target.classList.contains('MindmapEditor_canvas');
                        if (isOnBackground) {
                            e.preventDefault();
                        }
                    }}
                    onMouseMove={(e) => {
                        if (isPanning && panStart) {
                            const dx = e.clientX - panStart.x;
                            const dy = e.clientY - panStart.y;
                            panDistanceRef.current = Math.sqrt(dx * dx + dy * dy);
                            setCameraOffset({ 
                                x: panStart.offsetX + dx, 
                                y: panStart.offsetY + dy 
                            });
                            return;
                        }
                        if (isDrawing && workspaceRef.current) {
                            const rect = workspaceRef.current.getBoundingClientRect();
                            const worldPos = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
                            const lastPt = lastDrawPointRef.current;
                            if (lastPt) {
                                const dx = worldPos.x - lastPt.x;
                                const dy = worldPos.y - lastPt.y;
                                const dist = Math.sqrt(dx * dx + dy * dy);
                                if (dist >= MIN_POINT_DISTANCE) {
                                    drawingPathRef.current.push(worldPos);
                                    lastDrawPointRef.current = worldPos;
                                    if (drawingRafRef.current) cancelAnimationFrame(drawingRafRef.current);
                                    drawingRafRef.current = requestAnimationFrame(redrawCanvas);
                                }
                            }
                            return;
                        }
                        if (arrowStart && workspaceRef.current) {
                            const rect = workspaceRef.current.getBoundingClientRect();
                            const worldPos = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
                            setMousePos(worldPos);
                        }
                        if (editingArrowEnd && workspaceRef.current) {
                            const rect = workspaceRef.current.getBoundingClientRect();
                            const worldPos = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
                            setMousePos(worldPos);
                        }
                        if (draggingSegment && selectedArrowIndex !== null && workspaceRef.current) {
                            const rect = workspaceRef.current.getBoundingClientRect();
                            const currentMouse = { x: e.clientX - rect.left, y: e.clientY - rect.top };
                            const dx = currentMouse.x - draggingSegment.startMouse.x;
                            const dy = currentMouse.y - draggingSegment.startMouse.y;
                            const segIdx = draggingSegment.segmentIndex;
                            
                            const p1 = draggingSegment.origPath[segIdx];
                            const p2 = draggingSegment.origPath[segIdx + 1];
                            const isHorizontal = Math.abs(p2.y - p1.y) < Math.abs(p2.x - p1.x);
                            const freeMove = e.shiftKey;
                            
                            const newPath = draggingSegment.origPath.map((pt, idx) => {
                                if (idx === segIdx || idx === segIdx + 1) {
                                    if (freeMove) {
                                        return { x: pt.x + dx, y: pt.y + dy };
                                    } else if (isHorizontal) {
                                        return { x: pt.x, y: pt.y + dy };
                                    } else {
                                        return { x: pt.x + dx, y: pt.y };
                                    }
                                }
                                return pt;
                            });
                            
                            const newArrows = [...arrows];
                            newArrows[selectedArrowIndex] = {
                                ...newArrows[selectedArrowIndex],
                                manualPath: newPath
                            };
                            setArrows(newArrows);
                            arrowsRef.current = newArrows;
                        }
                        if (draggingWaypoint && selectedArrowIndex !== null && workspaceRef.current) {
                            const rect = workspaceRef.current.getBoundingClientRect();
                            const currentMouse = { x: e.clientX - rect.left, y: e.clientY - rect.top };
                            const dx = currentMouse.x - draggingWaypoint.startMouse.x;
                            const dy = currentMouse.y - draggingWaypoint.startMouse.y;
                            const wpIdx = draggingWaypoint.waypointIndex;
                            const origPt = draggingWaypoint.origPath[wpIdx];
                            
                            let newX = origPt.x + dx;
                            let newY = origPt.y + dy;
                            
                            if (e.shiftKey) {
                                const prevPt = draggingWaypoint.origPath[wpIdx - 1];
                                const nextPt = draggingWaypoint.origPath[wpIdx + 1];
                                const distToPrevX = Math.abs(newX - prevPt.x);
                                const distToPrevY = Math.abs(newY - prevPt.y);
                                const distToNextX = nextPt ? Math.abs(newX - nextPt.x) : Infinity;
                                const distToNextY = nextPt ? Math.abs(newY - nextPt.y) : Infinity;
                                
                                const minDist = Math.min(distToPrevX, distToPrevY, distToNextX, distToNextY);
                                if (minDist === distToPrevX) newX = prevPt.x;
                                else if (minDist === distToPrevY) newY = prevPt.y;
                                else if (minDist === distToNextX && nextPt) newX = nextPt.x;
                                else if (minDist === distToNextY && nextPt) newY = nextPt.y;
                            }
                            
                            const newPath = draggingWaypoint.origPath.map((pt, idx) => {
                                if (idx === wpIdx) {
                                    return { x: newX, y: newY };
                                }
                                return pt;
                            });
                            
                            const newArrows = [...arrows];
                            newArrows[selectedArrowIndex] = {
                                ...newArrows[selectedArrowIndex],
                                manualPath: newPath
                            };
                            setArrows(newArrows);
                            arrowsRef.current = newArrows;
                        }
                        if (selectionBox && workspaceRef.current) {
                            const rect = workspaceRef.current.getBoundingClientRect();
                            const worldPos = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
                            setSelectionBox(prev => prev ? { ...prev, currentX: worldPos.x, currentY: worldPos.y } : null);
                        }
                    }}
                    onMouseUp={() => {
                        if (isPanning) {
                            setIsPanning(false);
                            setPanStart(null);
                        }
                        if (isDrawing && drawingPathRef.current.length > 1) {
                            if (drawingRafRef.current) {
                                cancelAnimationFrame(drawingRafRef.current);
                                drawingRafRef.current = null;
                            }
                            
                            const simplifiedPath = simplifyPath(drawingPathRef.current, 2);
                            const pathData = simplifiedPath.reduce((acc, pt, idx) => {
                                return acc + (idx === 0 ? `M${pt.x},${pt.y}` : ` L${pt.x},${pt.y}`);
                            }, '');
                            
                            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
                            simplifiedPath.forEach(pt => {
                                minX = Math.min(minX, pt.x);
                                minY = Math.min(minY, pt.y);
                                maxX = Math.max(maxX, pt.x);
                                maxY = Math.max(maxY, pt.y);
                            });
                            const centerX = (minX + maxX) / 2;
                            const centerY = (minY + maxY) / 2;
                            const width = maxX - minX + drawingWidth * 2;
                            const height = maxY - minY + drawingWidth * 2;
                            
                            pushHistory();
                            const newObject: IMindmapObject = {
                                position: { x: centerX, y: centerY },
                                width,
                                height,
                                angle: 0,
                                text: '',
                                style: 'drawing',
                                pathData,
                                strokeColor: drawingColor,
                                strokeWidth: drawingWidth
                            };
                            const newObjects = [...objectsRef.current, newObject];
                            setObjects(newObjects);
                            objectsRef.current = newObjects;
                            debouncedSave(newObjects, arrowsRef.current, name);
                            
                            drawingPathRef.current = [];
                            lastDrawPointRef.current = null;
                            if (drawingCanvasRef.current) {
                                const ctx = drawingCanvasRef.current.getContext('2d');
                                if (ctx) ctx.clearRect(0, 0, drawingCanvasRef.current.width, drawingCanvasRef.current.height);
                            }
                            setIsDrawing(false);
                        } else if (isDrawing) {
                            drawingPathRef.current = [];
                            lastDrawPointRef.current = null;
                            if (drawingCanvasRef.current) {
                                const ctx = drawingCanvasRef.current.getContext('2d');
                                if (ctx) ctx.clearRect(0, 0, drawingCanvasRef.current.width, drawingCanvasRef.current.height);
                            }
                            setIsDrawing(false);
                        }
                        if (draggingSegment) {
                            setDraggingSegment(null);
                            debouncedSave(objectsRef.current, arrowsRef.current, name);
                        }
                        if (draggingWaypoint) {
                            setDraggingWaypoint(null);
                            debouncedSave(objectsRef.current, arrowsRef.current, name);
                        }
                        if (selectionBox) {
                            const minX = Math.min(selectionBox.startX, selectionBox.currentX);
                            const maxX = Math.max(selectionBox.startX, selectionBox.currentX);
                            const minY = Math.min(selectionBox.startY, selectionBox.currentY);
                            const maxY = Math.max(selectionBox.startY, selectionBox.currentY);
                            
                            const selected = new Set<number>();
                            objects.forEach((obj, idx) => {
                                const objCenterX = obj.position.x;
                                const objCenterY = obj.position.y;
                                // Use at least 10px dimensions for drawings with missing size
                                const objWidth = obj.width || (obj.style === 'drawing' ? 10 : 0);
                                const objHeight = obj.height || (obj.style === 'drawing' ? 10 : 0);
                                const objLeft = objCenterX - objWidth / 2;
                                const objRight = objCenterX + objWidth / 2;
                                const objTop = objCenterY - objHeight / 2;
                                const objBottom = objCenterY + objHeight / 2;
                                
                                const intersects = !(objRight < minX || objLeft > maxX || objBottom < minY || objTop > maxY);
                                if (intersects) {
                                    selected.add(idx);
                                }
                            });
                            
                            setSelectedIndices(selected);
                            setSelectionBox(null);
                        }
                    }}
                    onMouseLeave={() => {
                        if (isPanning) {
                            setIsPanning(false);
                            setPanStart(null);
                        }
                    }}>
                    <div className="MindmapEditor_canvas" />
                    <canvas 
                        ref={drawingCanvasRef}
                        className="MindmapEditor_drawing_canvas"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            pointerEvents: 'none',
                            zIndex: 1000
                        }}
                    />
                    <div 
                        className="MindmapEditor_camera"
                        style={{
                            transform: `translate(${cameraOffset.x}px, ${cameraOffset.y}px) scale(${zoom})`,
                            transformOrigin: '0 0'
                        }}
                    >
                    <svg className="MindmapEditor_arrows_layer" style={{ pointerEvents: 'none', overflow: 'visible' }}>
                        <defs>
                            <marker id="arrowhead-thin" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                                <polygon points="0 0, 10 3.5, 0 7" fill="#f90" />
                            </marker>
                            <marker id="arrowhead-thick" markerWidth="12" markerHeight="9" refX="11" refY="4.5" orient="auto">
                                <polygon points="0 0, 12 4.5, 0 9" fill="#f90" />
                            </marker>
                            <marker id="arrowhead-hollow" markerWidth="12" markerHeight="9" refX="11" refY="4.5" orient="auto">
                                <polygon points="0 0, 12 4.5, 0 9" fill="none" stroke="#f90" strokeWidth="1" />
                            </marker>
                            <marker id="arrowhead-straight-thin" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                                <polygon points="0 0, 10 3.5, 0 7" fill="#f90" />
                            </marker>
                            <marker id="arrowhead-straight-thick" markerWidth="12" markerHeight="9" refX="11" refY="4.5" orient="auto">
                                <polygon points="0 0, 12 4.5, 0 9" fill="#f90" />
                            </marker>
                            <marker id="arrowhead-straight-hollow" markerWidth="12" markerHeight="9" refX="11" refY="4.5" orient="auto">
                                <polygon points="0 0, 12 4.5, 0 9" fill="none" stroke="#f90" strokeWidth="1" />
                            </marker>
                            {/* Selected (blue) markers */}
                            <marker id="arrowhead-thin-selected" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                                <polygon points="0 0, 10 3.5, 0 7" fill="#0af" />
                            </marker>
                            <marker id="arrowhead-thick-selected" markerWidth="12" markerHeight="9" refX="11" refY="4.5" orient="auto">
                                <polygon points="0 0, 12 4.5, 0 9" fill="#0af" />
                            </marker>
                            <marker id="arrowhead-hollow-selected" markerWidth="12" markerHeight="9" refX="11" refY="4.5" orient="auto">
                                <polygon points="0 0, 12 4.5, 0 9" fill="none" stroke="#0af" strokeWidth="1" />
                            </marker>
                            <marker id="arrowhead-straight-thin-selected" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                                <polygon points="0 0, 10 3.5, 0 7" fill="#0af" />
                            </marker>
                            <marker id="arrowhead-straight-thick-selected" markerWidth="12" markerHeight="9" refX="11" refY="4.5" orient="auto">
                                <polygon points="0 0, 12 4.5, 0 9" fill="#0af" />
                            </marker>
                            <marker id="arrowhead-straight-hollow-selected" markerWidth="12" markerHeight="9" refX="11" refY="4.5" orient="auto">
                                <polygon points="0 0, 12 4.5, 0 9" fill="none" stroke="#0af" strokeWidth="1" />
                            </marker>
                            {/* Manual arrow markers */}
                            <marker id="arrowhead-manual-thin" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                                <polygon points="0 0, 10 3.5, 0 7" fill="#f90" />
                            </marker>
                            <marker id="arrowhead-manual-thick" markerWidth="12" markerHeight="9" refX="11" refY="4.5" orient="auto">
                                <polygon points="0 0, 12 4.5, 0 9" fill="#f90" />
                            </marker>
                            <marker id="arrowhead-manual-hollow" markerWidth="12" markerHeight="9" refX="11" refY="4.5" orient="auto">
                                <polygon points="0 0, 12 4.5, 0 9" fill="none" stroke="#f90" strokeWidth="1" />
                            </marker>
                            <marker id="arrowhead-manual-thin-selected" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                                <polygon points="0 0, 10 3.5, 0 7" fill="#0af" />
                            </marker>
                            <marker id="arrowhead-manual-thick-selected" markerWidth="12" markerHeight="9" refX="11" refY="4.5" orient="auto">
                                <polygon points="0 0, 12 4.5, 0 9" fill="#0af" />
                            </marker>
                            <marker id="arrowhead-manual-hollow-selected" markerWidth="12" markerHeight="9" refX="11" refY="4.5" orient="auto">
                                <polygon points="0 0, 12 4.5, 0 9" fill="none" stroke="#0af" strokeWidth="1" />
                            </marker>
                        </defs>
                        {arrows.map((arrow, arrowIdx) => {
                            const fromObj = objects[arrow.fromObjectIndex];
                            const toObj = objects[arrow.toObjectIndex];
                            if (!fromObj || !toObj) return null;
                            
                            const start = getSnapPointCoords(fromObj, arrow.fromSnapPoint);
                            const end = getSnapPointCoords(toObj, arrow.toSnapPoint);
                            const arrowStyle = arrow.style || 'thin';
                            const isBlock = arrowStyle === 'block';
                            const isStraight = typeof arrowStyle === 'string' && arrowStyle.startsWith('straight-');
                            const isManual = typeof arrowStyle === 'string' && arrowStyle.startsWith('manual-');
                            
                            const isSelected = selectedArrowIndex === arrowIdx;
                            const isEditingText = editingBlockArrowIndex === arrowIdx;
                            
                            if (isBlock) {
                                const dx = end.x - start.x;
                                const dy = end.y - start.y;
                                const length = Math.hypot(dx, dy);
                                const angle = Math.atan2(dy, dx);
                                const arrowHeight = 24;
                                const arrowHeadLength = 20;
                                const bodyLength = Math.max(0, length - arrowHeadLength);
                                
                                const points = [
                                    { x: 0, y: -arrowHeight / 2 },
                                    { x: bodyLength, y: -arrowHeight / 2 },
                                    { x: bodyLength, y: -arrowHeight / 2 - 6 },
                                    { x: length, y: 0 },
                                    { x: bodyLength, y: arrowHeight / 2 + 6 },
                                    { x: bodyLength, y: arrowHeight / 2 },
                                    { x: 0, y: arrowHeight / 2 },
                                ];
                                
                                const rotatedPoints = points.map(p => ({
                                    x: start.x + p.x * Math.cos(angle) - p.y * Math.sin(angle),
                                    y: start.y + p.x * Math.sin(angle) + p.y * Math.cos(angle)
                                }));
                                
                                const pointsStr = rotatedPoints.map(p => `${p.x},${p.y}`).join(' ');
                                const centerX = start.x + (bodyLength / 2) * Math.cos(angle);
                                const centerY = start.y + (bodyLength / 2) * Math.sin(angle);
                                const angleDeg = angle * 180 / Math.PI;
                                
                                return (
                                    <g key={arrow.id} style={{ pointerEvents: 'all' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedArrowIndex(arrowIdx);
                                            setEditingArrowEnd(null);
                                            workspaceRef.current?.focus();
                                        }}
                                        onDoubleClick={(e) => {
                                            e.stopPropagation();
                                            setEditingBlockArrowIndex(arrowIdx);
                                        }}
                                        onContextMenu={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const newArrows = arrows.filter(a => a.id !== arrow.id);
                                            updateArrows(newArrows);
                                            setSelectedArrowIndex(null);
                                            setEditingBlockArrowIndex(null);
                                        }}>
                                        <polygon 
                                            points={pointsStr}
                                            fill={isSelected ? '#0af' : '#f90'}
                                            stroke={isSelected ? '#08d' : '#d70'}
                                            strokeWidth={1}
                                            style={{ cursor: 'pointer' }}
                                        />
                                        {isEditingText ? (
                                            <foreignObject
                                                x={centerX - bodyLength / 2 + 5}
                                                y={centerY - arrowHeight / 2}
                                                width={bodyLength - 10}
                                                height={arrowHeight}
                                                transform={`rotate(${angleDeg}, ${centerX}, ${centerY})`}
                                            >
                                                <input
                                                    type="text"
                                                    autoFocus
                                                    defaultValue={arrow.text || ''}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onBlur={(e) => {
                                                        const newArrows = [...arrows];
                                                        newArrows[arrowIdx] = { ...arrow, text: e.target.value };
                                                        updateArrows(newArrows);
                                                        setEditingBlockArrowIndex(null);
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            const newArrows = [...arrows];
                                                            newArrows[arrowIdx] = { ...arrow, text: (e.target as HTMLInputElement).value };
                                                            updateArrows(newArrows);
                                                            setEditingBlockArrowIndex(null);
                                                        }
                                                        if (e.key === 'Escape') {
                                                            setEditingBlockArrowIndex(null);
                                                        }
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        border: 'none',
                                                        background: 'transparent',
                                                        color: '#fff',
                                                        textAlign: 'center',
                                                        fontSize: '12px',
                                                        fontWeight: 'bold',
                                                        outline: 'none',
                                                    }}
                                                />
                                            </foreignObject>
                                        ) : (
                                            <text
                                                x={centerX}
                                                y={centerY}
                                                textAnchor="middle"
                                                dominantBaseline="middle"
                                                fill="#fff"
                                                fontSize="12"
                                                fontWeight="bold"
                                                transform={`rotate(${angleDeg}, ${centerX}, ${centerY})`}
                                                style={{ pointerEvents: 'none' }}
                                            >
                                                {arrow.text || ''}
                                            </text>
                                        )}
                                    </g>
                                );
                            }
                            
                            let path: { x: number; y: number }[];
                            if (isManual && arrow.manualPath && arrow.manualPath.length >= 2) {
                                path = [...arrow.manualPath];
                                path[0] = start;
                                path[path.length - 1] = end;
                            } else if (isStraight) {
                                path = [start, end];
                            } else {
                                path = calculateAutoPathWithSnaps(start, end, arrow.fromSnapPoint, arrow.toSnapPoint, objects, arrow.fromObjectIndex, arrow.toObjectIndex) || calculateAutoPath(start, end);
                            }
                            const d = pathToSvg(path);
                            
                            const baseStyle = isManual ? arrowStyle.replace('manual-', '') : (isStraight ? arrowStyle.replace('straight-', '') : arrowStyle);
                            const strokeWidth = baseStyle === 'thin' ? 1 : baseStyle === 'thick' ? 3 : 2;
                            const markerId = `arrowhead-${arrowStyle}${isSelected ? '-selected' : ''}`;
                            
                            return (
                                <g key={arrow.id} style={{ pointerEvents: 'stroke' }} 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedArrowIndex(arrowIdx);
                                        setEditingArrowEnd(null);
                                        workspaceRef.current?.focus();
                                    }}
                                    onDoubleClick={(e) => {
                                        if (!isManual || !arrow.manualPath) return;
                                        e.stopPropagation();
                                        const rect = workspaceRef.current?.getBoundingClientRect();
                                        if (!rect) return;
                                        const clickX = e.clientX - rect.left;
                                        const clickY = e.clientY - rect.top;
                                        
                                        let bestSegIdx = 0;
                                        let bestDist = Infinity;
                                        for (let i = 0; i < path.length - 1; i++) {
                                            const p1 = path[i];
                                            const p2 = path[i + 1];
                                            const midX = (p1.x + p2.x) / 2;
                                            const midY = (p1.y + p2.y) / 2;
                                            const dist = Math.hypot(clickX - midX, clickY - midY);
                                            if (dist < bestDist) {
                                                bestDist = dist;
                                                bestSegIdx = i;
                                            }
                                        }
                                        
                                        const newPath = [...path];
                                        newPath.splice(bestSegIdx + 1, 0, { x: clickX, y: clickY });
                                        
                                        const newArrows = [...arrows];
                                        newArrows[arrowIdx] = { ...arrow, manualPath: newPath };
                                        updateArrows(newArrows);
                                        setSelectedArrowIndex(arrowIdx);
                                    }}
                                    onContextMenu={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (selectedArrowIndex !== arrowIdx) {
                                            const newArrows = arrows.filter(a => a.id !== arrow.id);
                                            updateArrows(newArrows);
                                            setSelectedArrowIndex(null);
                                            setEditingArrowEnd(null);
                                        }
                                    }}>
                                    <path 
                                        d={d} 
                                        stroke="transparent" 
                                        strokeWidth={12} 
                                        fill="none"
                                        style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
                                    />
                                    <path 
                                        d={d} 
                                        stroke={isSelected ? '#0af' : '#f90'} 
                                        strokeWidth={isSelected ? strokeWidth + 1 : strokeWidth} 
                                        fill="none"
                                        markerEnd={`url(#${markerId})`}
                                        style={{ pointerEvents: 'none' }}
                                    />
                                </g>
                            );
                        })}
                        {arrowStart && mousePos && objects[arrowStart.objectIndex] && (() => {
                            const startPt = getSnapPointCoords(objects[arrowStart.objectIndex], arrowStart.snapPoint);
                            const isStraightMode = arrowMode && arrowMode.startsWith('straight-');
                            const isBlockMode = arrowMode === 'block';
                            
                            if (isBlockMode) {
                                let endPt = mousePos;
                                if (hoveredObject !== null && hoveredObject !== arrowStart.objectIndex && objects[hoveredObject]) {
                                    const targetSnapPoints = getSnapPoints(objects[hoveredObject]);
                                    let bestSnap = targetSnapPoints[0];
                                    let bestDist = Infinity;
                                    for (const sp of targetSnapPoints) {
                                        const d = Math.hypot(sp.x - mousePos.x, sp.y - mousePos.y);
                                        if (d < bestDist) { bestDist = d; bestSnap = sp; }
                                    }
                                    endPt = { x: bestSnap.x, y: bestSnap.y };
                                }
                                
                                const dx = endPt.x - startPt.x;
                                const dy = endPt.y - startPt.y;
                                const length = Math.hypot(dx, dy);
                                const angle = Math.atan2(dy, dx);
                                const arrowHeight = 24;
                                const arrowHeadLength = 20;
                                const bodyLength = Math.max(0, length - arrowHeadLength);
                                
                                const points = [
                                    { x: 0, y: -arrowHeight / 2 },
                                    { x: bodyLength, y: -arrowHeight / 2 },
                                    { x: bodyLength, y: -arrowHeight / 2 - 6 },
                                    { x: length, y: 0 },
                                    { x: bodyLength, y: arrowHeight / 2 + 6 },
                                    { x: bodyLength, y: arrowHeight / 2 },
                                    { x: 0, y: arrowHeight / 2 },
                                ];
                                
                                const rotatedPoints = points.map(p => ({
                                    x: startPt.x + p.x * Math.cos(angle) - p.y * Math.sin(angle),
                                    y: startPt.y + p.x * Math.sin(angle) + p.y * Math.cos(angle)
                                }));
                                
                                const pointsStr = rotatedPoints.map(p => `${p.x},${p.y}`).join(' ');
                                
                                return (
                                    <polygon
                                        points={pointsStr}
                                        fill="#f90"
                                        fillOpacity={0.5}
                                        stroke="#f90"
                                        strokeWidth={2}
                                        strokeDasharray="5,5"
                                    />
                                );
                            }
                            
                            let previewPath: { x: number; y: number }[];
                            if (isStraightMode) {
                                previewPath = [startPt, mousePos];
                            } else if (hoveredObject !== null && hoveredObject !== arrowStart.objectIndex && objects[hoveredObject]) {
                                const targetSnapPoints = getSnapPoints(objects[hoveredObject]);
                                let bestSnap = targetSnapPoints[0];
                                let bestDist = Infinity;
                                for (const sp of targetSnapPoints) {
                                    const d = Math.hypot(sp.x - mousePos.x, sp.y - mousePos.y);
                                    if (d < bestDist) { bestDist = d; bestSnap = sp; }
                                }
                                previewPath = calculateAutoPathWithSnaps(
                                    startPt,
                                    { x: bestSnap.x, y: bestSnap.y },
                                    arrowStart.snapPoint,
                                    bestSnap.id,
                                    objects,
                                    arrowStart.objectIndex,
                                    hoveredObject
                                ) || calculateAutoPath(startPt, { x: bestSnap.x, y: bestSnap.y });
                            } else {
                                previewPath = calculateAutoPathWithSnaps(
                                    startPt,
                                    mousePos,
                                    arrowStart.snapPoint,
                                    'top',
                                    objects,
                                    arrowStart.objectIndex,
                                    -1
                                ) || calculateAutoPath(startPt, mousePos);
                            }
                            
                            return (
                                <path 
                                    d={pathToSvg(previewPath)}
                                    stroke="#f90" 
                                    strokeWidth="2" 
                                    strokeDasharray="5,5"
                                    fill="none"
                                />
                            );
                        })()}
                        {editingArrowEnd && selectedArrowIndex !== null && arrows[selectedArrowIndex] && mousePos && (() => {
                            const editArrow = arrows[selectedArrowIndex];
                            const editArrowStyle = editArrow.style || 'thin';
                            const isEditStraight = typeof editArrowStyle === 'string' && editArrowStyle.startsWith('straight-');
                            
                            let previewPath: { x: number; y: number }[];
                            
                            if (isEditStraight) {
                                const fromObj = objects[editArrow.fromObjectIndex];
                                const toObj = objects[editArrow.toObjectIndex];
                                const startPt = editingArrowEnd === 'start' || !fromObj
                                    ? mousePos 
                                    : getSnapPointCoords(fromObj, editArrow.fromSnapPoint);
                                const endPt = editingArrowEnd === 'end' || !toObj
                                    ? mousePos 
                                    : getSnapPointCoords(toObj, editArrow.toSnapPoint);
                                previewPath = [startPt, endPt];
                            } else if (hoveredObject !== null && objects[hoveredObject]) {
                                const targetSnapPoints = getSnapPoints(objects[hoveredObject]);
                                let bestSnap = targetSnapPoints[0];
                                let bestDist = Infinity;
                                for (const sp of targetSnapPoints) {
                                    const d = Math.hypot(sp.x - mousePos.x, sp.y - mousePos.y);
                                    if (d < bestDist) { bestDist = d; bestSnap = sp; }
                                }
                                const fromIdx = editingArrowEnd === 'start' ? hoveredObject : editArrow.fromObjectIndex;
                                const toIdx = editingArrowEnd === 'end' ? hoveredObject : editArrow.toObjectIndex;
                                if (!objects[fromIdx] || !objects[toIdx]) {
                                    previewPath = [mousePos, mousePos];
                                } else {
                                    const fromSnap = editingArrowEnd === 'start' ? bestSnap.id : editArrow.fromSnapPoint;
                                    const toSnap = editingArrowEnd === 'end' ? bestSnap.id : editArrow.toSnapPoint;
                                    const startPt = getSnapPointCoords(objects[fromIdx], fromSnap);
                                    const endPt = getSnapPointCoords(objects[toIdx], toSnap);
                                    previewPath = calculateAutoPathWithSnaps(startPt, endPt, fromSnap, toSnap, objects, fromIdx, toIdx) || calculateAutoPath(startPt, endPt);
                                }
                            } else {
                                const fromObj = objects[editArrow.fromObjectIndex];
                                const toObj = objects[editArrow.toObjectIndex];
                                if (!fromObj && editingArrowEnd !== 'start') {
                                    previewPath = [mousePos, mousePos];
                                } else if (!toObj && editingArrowEnd !== 'end') {
                                    previewPath = [mousePos, mousePos];
                                } else {
                                    const startPt = editingArrowEnd === 'start' 
                                        ? mousePos 
                                        : getSnapPointCoords(fromObj, editArrow.fromSnapPoint);
                                    const endPt = editingArrowEnd === 'end' 
                                        ? mousePos 
                                        : getSnapPointCoords(toObj, editArrow.toSnapPoint);
                                    const fromSnap = editingArrowEnd === 'start' ? 'top' as SnapPointId : editArrow.fromSnapPoint;
                                    const toSnap = editingArrowEnd === 'end' ? 'top' as SnapPointId : editArrow.toSnapPoint;
                                    previewPath = calculateAutoPathWithSnaps(startPt, endPt, fromSnap, toSnap, objects, -1, -1) || calculateAutoPath(startPt, endPt);
                                }
                            }
                            
                            return (
                                <path 
                                    d={pathToSvg(previewPath)}
                                    stroke="#0af" 
                                    strokeWidth="2" 
                                    strokeDasharray="5,5"
                                    fill="none"
                                />
                            );
                        })()}
                    </svg>
                    
                    {/* Drawing layer for freehand drawings */}
                    <svg className="MindmapEditor_drawings_layer" style={{ pointerEvents: 'none', overflow: 'visible' }}>
                        {/* Saved drawings */}
                        {objects.map((obj, idx) => {
                            if (obj.style !== 'drawing' || !obj.pathData) return null;
                            const isSelected = selectedObjectIndex === idx;
                            const isMultiSelected = selectedIndices.has(idx);
                            const offsetX = obj.drawingOffset?.x || 0;
                            const offsetY = obj.drawingOffset?.y || 0;
                            return (
                                <g key={`drawing-${idx}`} transform={`translate(${offsetX}, ${offsetY})`}>
                                    <path
                                        d={obj.pathData}
                                        stroke={isSelected ? '#0af' : (isMultiSelected ? '#0af' : (obj.strokeColor || '#333'))}
                                        strokeWidth={obj.strokeWidth || 3}
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedObjectIndex(idx);
                                            setSelectedArrowIndex(null);
                                            setSelectedIndices(new Set());
                                        }}
                                    />
                                </g>
                            );
                        })}
                    </svg>
                    
                    {selectedArrowIndex !== null && arrows[selectedArrowIndex] && !editingArrowEnd && !draggingSegment && !draggingWaypoint && (() => {
                        const arrow = arrows[selectedArrowIndex];
                        const fromObj = objects[arrow.fromObjectIndex];
                        const toObj = objects[arrow.toObjectIndex];
                        if (!fromObj || !toObj) return null;
                        const startCoords = getSnapPointCoords(fromObj, arrow.fromSnapPoint);
                        const endCoords = getSnapPointCoords(toObj, arrow.toSnapPoint);
                        const arrowStyle = arrow.style || 'thin';
                        const isManualArrow = typeof arrowStyle === 'string' && arrowStyle.startsWith('manual-');
                        
                        let segmentHandles: React.ReactNode[] = [];
                        let waypointHandles: React.ReactNode[] = [];
                        if (isManualArrow && arrow.manualPath && arrow.manualPath.length >= 2) {
                            const path = [...arrow.manualPath];
                            path[0] = startCoords;
                            path[path.length - 1] = endCoords;
                            
                            for (let segIdx = 0; segIdx < path.length - 1; segIdx++) {
                                const p1 = path[segIdx];
                                const p2 = path[segIdx + 1];
                                const midX = (p1.x + p2.x) / 2;
                                const midY = (p1.y + p2.y) / 2;
                                
                                segmentHandles.push(
                                    <div 
                                        key={`seg-${segIdx}`}
                                        className="MindmapEditor_segment_handle"
                                        style={{ left: midX, top: midY }}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const rect = workspaceRef.current?.getBoundingClientRect();
                                            if (!rect) return;
                                            setDraggingSegment({
                                                segmentIndex: segIdx,
                                                startMouse: { x: e.clientX - rect.left, y: e.clientY - rect.top },
                                                origPath: path
                                            });
                                        }}
                                        onContextMenu={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (path.length <= 2) {
                                                const newArrows = arrows.filter((_, idx) => idx !== selectedArrowIndex);
                                                updateArrows(newArrows);
                                                setSelectedArrowIndex(null);
                                            } else {
                                                const midPoint = { x: (path[segIdx].x + path[segIdx + 1].x) / 2, y: (path[segIdx].y + path[segIdx + 1].y) / 2 };
                                                const newPath = path.filter((_, idx) => idx !== segIdx && idx !== segIdx + 1);
                                                newPath.splice(segIdx, 0, midPoint);
                                                const newArrows = [...arrows];
                                                newArrows[selectedArrowIndex] = { ...arrow, manualPath: newPath };
                                                updateArrows(newArrows);
                                            }
                                        }}
                                    />
                                );
                            }
                            
                            for (let wpIdx = 1; wpIdx < path.length - 1; wpIdx++) {
                                const pt = path[wpIdx];
                                waypointHandles.push(
                                    <div 
                                        key={`wp-${wpIdx}`}
                                        className="MindmapEditor_waypoint_handle"
                                        style={{ left: pt.x, top: pt.y }}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const rect = workspaceRef.current?.getBoundingClientRect();
                                            if (!rect) return;
                                            setDraggingWaypoint({
                                                waypointIndex: wpIdx,
                                                startMouse: { x: e.clientX - rect.left, y: e.clientY - rect.top },
                                                origPath: path
                                            });
                                        }}
                                        onContextMenu={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (path.length <= 2) {
                                                const newArrows = arrows.filter((_, idx) => idx !== selectedArrowIndex);
                                                updateArrows(newArrows);
                                                setSelectedArrowIndex(null);
                                            } else {
                                                const newPath = path.filter((_, idx) => idx !== wpIdx);
                                                const newArrows = [...arrows];
                                                newArrows[selectedArrowIndex] = { ...arrow, manualPath: newPath };
                                                updateArrows(newArrows);
                                            }
                                        }}
                                    />
                                );
                            }
                        }
                        
                        return (
                            <>
                                <div 
                                    className="MindmapEditor_arrow_endpoint MindmapEditor_arrow_endpoint--start"
                                    style={{ left: startCoords.x, top: startCoords.y }}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setEditingArrowEnd('start');
                                        workspaceRef.current?.focus();
                                    }}
                                />
                                <div 
                                    className="MindmapEditor_arrow_endpoint MindmapEditor_arrow_endpoint--end"
                                    style={{ left: endCoords.x, top: endCoords.y }}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setEditingArrowEnd('end');
                                        workspaceRef.current?.focus();
                                    }}
                                />
                                {segmentHandles}
                                {waypointHandles}
                            </>
                        );
                    })()}
                    {/* Bounding boxes for selected/multi-selected drawings */}
                    {objects.map((it, i) => {
                        if (it.style !== 'drawing') return null;
                        const isSelected = selectedObjectIndex === i;
                        const isMultiSelected = selectedIndices.has(i);
                        if (!isSelected && !isMultiSelected) return null;
                        
                        return (
                            <div
                                key={`drawing-selection-box-${i}`}
                                className="MindmapEditor_drawing_selection_box"
                                style={{
                                    position: 'absolute',
                                    top: (it.position.y - it.height / 2) + 'px',
                                    left: (it.position.x - it.width / 2) + 'px',
                                    width: it.width + 'px',
                                    height: it.height + 'px',
                                    background: 'transparent',
                                    border: '4px dashed #0af',
                                    cursor: 'move',
                                    boxSizing: 'border-box'
                                }}
                                onMouseDown={(downEvent) => {
                                    if (downEvent.button !== 0) return;
                                    downEvent.stopPropagation();
                                    
                                    const startX = downEvent.clientX;
                                    const startY = downEvent.clientY;
                                    
                                    // Determine which indices to move (multi-select or single)
                                    const indicesToMove = isMultiSelected ? Array.from(selectedIndices) : [i];
                                    
                                    // Store original positions and offsets
                                    const origData = indicesToMove.map(idx => ({
                                        idx,
                                        pos: { ...objectsRef.current[idx].position },
                                        offset: objectsRef.current[idx].drawingOffset || { x: 0, y: 0 }
                                    }));
                                    
                                    let moved = false;
                                    
                                    const onMove = (moveEvent: MouseEvent) => {
                                        const dx = (moveEvent.clientX - startX) / zoom;
                                        const dy = (moveEvent.clientY - startY) / zoom;
                                        if (Math.abs(dx) > 2 || Math.abs(dy) > 2) moved = true;
                                        
                                        const newObjects = [...objectsRef.current];
                                        origData.forEach(({ idx, pos, offset }) => {
                                            const obj = newObjects[idx];
                                            if (obj) {
                                                if (obj.style === 'drawing') {
                                                    newObjects[idx] = { 
                                                        ...obj, 
                                                        position: { x: pos.x + dx, y: pos.y + dy },
                                                        drawingOffset: { x: offset.x + dx, y: offset.y + dy }
                                                    };
                                                } else {
                                                    newObjects[idx] = { 
                                                        ...obj, 
                                                        position: { x: pos.x + dx, y: pos.y + dy }
                                                    };
                                                }
                                            }
                                        });
                                        setObjects(newObjects);
                                        objectsRef.current = newObjects;
                                    };
                                    
                                    const onUp = () => {
                                        document.removeEventListener('mousemove', onMove);
                                        document.removeEventListener('mouseup', onUp);
                                        if (moved) {
                                            debouncedSave(objectsRef.current, arrowsRef.current, name);
                                        }
                                    };
                                    
                                    document.addEventListener('mousemove', onMove);
                                    document.addEventListener('mouseup', onUp);
                                }}
                            />
                        );
                    })}
                    {objects.map((it, i) => {
                        if (it.style === 'drawing') return null;
                        const extraClasses = [
                            it.noteId ? 'MindmapEditor_object--note' : '',
                            it.taskId ? 'MindmapEditor_object--task' : '',
                            it.taskChecked ? 'MindmapEditor_object--task-checked' : '',
                            selectedObjectIndex === i ? 'MindmapEditor_object--selected' : '',
                            selectedIndices.has(i) ? 'MindmapEditor_object--multi-selected' : ''
                        ].filter(Boolean).join(' ');
                        return <div className={`MindmapEditor_object MindmapEditor_object--${it.style} ${extraClasses}`}
                            key={i}
                            style={{
                                top: (it.position.y - it.height / 2) + "px",
                                left: (it.position.x - it.width / 2) + 'px',
                                width: it.width + 'px',
                                height: it.height + 'px',
                                transform: `rotate(${it.angle || 0}deg)`,
                                ...(['triangle', 'trapezoid', 'star'].includes(it.style) ? {} : {
                                    ...(it.fillColor && (it.style === 'sticker' || it.style === 'note') ? { background: `linear-gradient(135deg, ${it.fillColor}cc 0%, ${it.fillColor} 100%)` } : {}),
                                    ...(it.fillColor && it.style !== 'sticker' && it.style !== 'note' ? { backgroundColor: it.fillColor } : {}),
                                    ...(it.borderColor ? { borderColor: it.borderColor } : {}),
                                    ...(it.borderWidth !== undefined ? { borderWidth: it.borderWidth + 'px', borderStyle: 'solid' } : {})
                                })
                            }}
                            onMouseEnter={() => arrowMode && setHoveredObject(i)}
                            onMouseLeave={() => setHoveredObject(null)}
                            onMouseDown={(downEvent) => {
                                if (downEvent.button !== 0) return;
                                if ((downEvent.target as HTMLElement).classList.contains('MindmapEditor_object_marker')) return;
                                if ((downEvent.target as HTMLElement).classList.contains('MindmapEditor_snap_point')) return;
                                
                                const isTextarea = (downEvent.target as HTMLElement).tagName === 'TEXTAREA';
                                if (isTextarea && editingIndex === i) {
                                    return;
                                }
                                
                                if (arrowMode) {
                                    return;
                                }
                                
                                const isMediaWithControls = it.style === 'media' && (it.mediaType === 'audio' || it.mediaType === 'video');
                                const DRAG_THRESHOLD = 5;
                                
                                if (isMediaWithControls) {
                                    const startX = downEvent.clientX;
                                    const startY = downEvent.clientY;
                                    let isDragging = false;
                                    let historyPushed = false;
                                    
                                    const isMultiSelected = selectedIndices.has(i);
                                    const indicesToMove = isMultiSelected ? Array.from(selectedIndices) : [i];
                                    
                                    const moveHandler = (moveEvent: MouseEvent) => {
                                        const dx = moveEvent.clientX - startX;
                                        const dy = moveEvent.clientY - startY;
                                        const distance = Math.sqrt(dx * dx + dy * dy);
                                        
                                        if (!isDragging && distance > DRAG_THRESHOLD) {
                                            isDragging = true;
                                            if (!historyPushed) {
                                                pushHistory();
                                                historyPushed = true;
                                            }
                                            setDraggingIndex(i);
                                            if (!isMultiSelected) {
                                                if (selectedObjectIndex !== i) {
                                                    setSelectedObjectIndex(i);
                                                    setSelectedArrowIndex(null);
                                                }
                                                setSelectedIndices(new Set());
                                            }
                                        }
                                        
                                        if (isDragging) {
                                            const moveDx = moveEvent.movementX / zoom;
                                            const moveDy = moveEvent.movementY / zoom;
                                            setObjects(last => {
                                                const next = [...last];
                                                indicesToMove.forEach(idx => {
                                                    next[idx] = {
                                                        ...next[idx],
                                                        position: {
                                                            x: next[idx].position.x + moveDx,
                                                            y: next[idx].position.y + moveDy
                                                        }
                                                    };
                                                });
                                                objectsRef.current = next;
                                                return next;
                                            });
                                        }
                                    };
                                    
                                    const upHandler = () => {
                                        window.removeEventListener('mousemove', moveHandler);
                                        window.removeEventListener('mouseup', upHandler);
                                        if (isDragging) {
                                            setDraggingIndex(null);
                                            debouncedSave(objectsRef.current, arrowsRef.current, nameRef.current);
                                        } else {
                                            if (selectedObjectIndex !== i) {
                                                setSelectedObjectIndex(i);
                                                setSelectedArrowIndex(null);
                                                setSelectedIndices(new Set());
                                            }
                                        }
                                    };
                                    
                                    window.addEventListener('mousemove', moveHandler);
                                    window.addEventListener('mouseup', upHandler);
                                } else {
                                    downEvent.preventDefault();
                                    setDraggingIndex(i);
                                    
                                    const isMultiSelected = selectedIndices.has(i);
                                    if (!isMultiSelected) {
                                        if (selectedObjectIndex !== i) {
                                            setSelectedObjectIndex(i);
                                            setSelectedArrowIndex(null);
                                        }
                                        setSelectedIndices(new Set());
                                    }
                                    
                                    const indicesToMove = isMultiSelected ? Array.from(selectedIndices) : [i];
                                    pushHistory();
                                    
                                    const moveHandler = (moveEvent: MouseEvent) => {
                                        const dx = moveEvent.movementX / zoom;
                                        const dy = moveEvent.movementY / zoom;
                                        setObjects(last => {
                                            const next = [...last];
                                            indicesToMove.forEach(idx => {
                                                next[idx] = {
                                                    ...next[idx],
                                                    position: {
                                                        x: next[idx].position.x + dx,
                                                        y: next[idx].position.y + dy
                                                    }
                                                };
                                            });
                                            objectsRef.current = next;
                                            return next;
                                        });
                                    };
                                    
                                    const upHandler = () => {
                                        window.removeEventListener('mousemove', moveHandler);
                                        window.removeEventListener('mouseup', upHandler);
                                        setDraggingIndex(null);
                                        debouncedSave(objectsRef.current, arrowsRef.current, nameRef.current);
                                    };
                                    
                                    window.addEventListener('mousemove', moveHandler);
                                    window.addEventListener('mouseup', upHandler);
                                }
                            }}
                            onDoubleClick={() => {
                                if (it.style === 'folder') {
                                    if (it.folderId) {
                                        window.open(`#/folder/${it.folderId}`, '_blank');
                                    }
                                    return;
                                }
                                setEditingIndex(i);
                                setTimeout(() => {
                                    const el = textareaRefs.current[i];
                                    if (el) {
                                        el.focus();
                                        const selection = document.getSelection();
                                        if (selection) {
                                            const range = document.createRange();
                                            range.selectNodeContents(el);
                                            range.collapse(false);
                                            selection.removeAllRanges();
                                            selection.addRange(range);
                                        }
                                    }
                                }, 0);
                            }}
                            onContextMenu={(e) => handleContextMenu(e, i)}>
                            {['triangle', 'trapezoid', 'star'].includes(it.style) && (
                                <svg 
                                    className="MindmapEditor_object_svg"
                                    viewBox="0 0 100 100" 
                                    preserveAspectRatio="none"
                                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 0 }}
                                >
                                    {it.style === 'triangle' && (
                                        <polygon 
                                            points="50,0 0,100 100,100" 
                                            fill={it.fillColor || '#ffcc00'} 
                                            stroke={it.borderColor || '#f90'} 
                                            strokeWidth={(it.borderWidth ?? 2) * 100 / Math.min(it.width, it.height)}
                                            vectorEffect="non-scaling-stroke"
                                        />
                                    )}
                                    {it.style === 'trapezoid' && (
                                        <polygon 
                                            points="20,0 80,0 100,100 0,100" 
                                            fill={it.fillColor || '#ffcc00'} 
                                            stroke={it.borderColor || '#f90'} 
                                            strokeWidth={(it.borderWidth ?? 2) * 100 / Math.min(it.width, it.height)}
                                            vectorEffect="non-scaling-stroke"
                                        />
                                    )}
                                    {it.style === 'star' && (
                                        <polygon 
                                            points="50,0 61,35 98,35 68,57 79,91 50,70 21,91 32,57 2,35 39,35" 
                                            fill={it.fillColor || '#ffcc00'} 
                                            stroke={it.borderColor || '#f90'} 
                                            strokeWidth={(it.borderWidth ?? 2) * 100 / Math.min(it.width, it.height)}
                                            vectorEffect="non-scaling-stroke"
                                        />
                                    )}
                                </svg>
                            )}
                            {it.style === 'media' && it.mediaUrl ? (
                                <div className="MindmapEditor_media_content">
                                    {it.mediaType === 'image' && (
                                        <img 
                                            src={it.mediaUrl} 
                                            alt="" 
                                            className="MindmapEditor_media_image"
                                            draggable={false}
                                        />
                                    )}
                                    {it.mediaType === 'video' && (
                                        <div className="MindmapEditor_video_wrapper">
                                            <video 
                                                src={it.mediaUrl} 
                                                className="MindmapEditor_media_video"
                                                controls
                                                draggable={false}
                                            />
                                            <div 
                                                className="MindmapEditor_video_drag_overlay"
                                                onMouseDown={(e) => {
                                                    if (e.button !== 0) return;
                                                    e.stopPropagation();
                                                    
                                                    const startX = e.clientX;
                                                    const startY = e.clientY;
                                                    let isDragging = false;
                                                    let historyPushed = false;
                                                    const videoEl = (e.currentTarget as HTMLElement).previousElementSibling as HTMLVideoElement;
                                                    
                                                    const isMultiSelected = selectedIndices.has(i);
                                                    const indicesToMove = isMultiSelected ? Array.from(selectedIndices) : [i];
                                                    
                                                    const moveHandler = (moveEvent: MouseEvent) => {
                                                        const dx = moveEvent.clientX - startX;
                                                        const dy = moveEvent.clientY - startY;
                                                        const distance = Math.sqrt(dx * dx + dy * dy);
                                                        
                                                        if (!isDragging && distance > 5) {
                                                            isDragging = true;
                                                            if (!historyPushed) {
                                                                pushHistory();
                                                                historyPushed = true;
                                                            }
                                                            setDraggingIndex(i);
                                                            if (!isMultiSelected) {
                                                                if (selectedObjectIndex !== i) {
                                                                    setSelectedObjectIndex(i);
                                                                    setSelectedArrowIndex(null);
                                                                }
                                                                setSelectedIndices(new Set());
                                                            }
                                                        }
                                                        
                                                        if (isDragging) {
                                                            const moveDx = moveEvent.movementX / zoom;
                                                            const moveDy = moveEvent.movementY / zoom;
                                                            setObjects(last => {
                                                                const next = [...last];
                                                                indicesToMove.forEach(idx => {
                                                                    next[idx] = {
                                                                        ...next[idx],
                                                                        position: {
                                                                            x: next[idx].position.x + moveDx,
                                                                            y: next[idx].position.y + moveDy
                                                                        }
                                                                    };
                                                                });
                                                                objectsRef.current = next;
                                                                return next;
                                                            });
                                                        }
                                                    };
                                                    
                                                    const upHandler = () => {
                                                        window.removeEventListener('mousemove', moveHandler);
                                                        window.removeEventListener('mouseup', upHandler);
                                                        if (isDragging) {
                                                            setDraggingIndex(null);
                                                            debouncedSave(objectsRef.current, arrowsRef.current, nameRef.current);
                                                        } else {
                                                            if (selectedObjectIndex !== i) {
                                                                setSelectedObjectIndex(i);
                                                                setSelectedArrowIndex(null);
                                                                setSelectedIndices(new Set());
                                                            } else {
                                                                if (videoEl) {
                                                                    if (videoEl.paused) {
                                                                        videoEl.play();
                                                                    } else {
                                                                        videoEl.pause();
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    };
                                                    
                                                    window.addEventListener('mousemove', moveHandler);
                                                    window.addEventListener('mouseup', upHandler);
                                                }}
                                            />
                                        </div>
                                    )}
                                    {it.mediaType === 'audio' && (
                                        <div className="MindmapEditor_audio_wrapper">
                                            <audio 
                                                src={it.mediaUrl} 
                                                className="MindmapEditor_media_audio"
                                                controls
                                            />
                                            <div 
                                                className="MindmapEditor_audio_drag_overlay"
                                                onMouseDown={(e) => {
                                                    if (e.button !== 0) return;
                                                    e.stopPropagation();
                                                    
                                                    const startX = e.clientX;
                                                    const startY = e.clientY;
                                                    let isDragging = false;
                                                    let historyPushed = false;
                                                    const audioEl = (e.currentTarget as HTMLElement).previousElementSibling as HTMLAudioElement;
                                                    
                                                    const isMultiSelected = selectedIndices.has(i);
                                                    const indicesToMove = isMultiSelected ? Array.from(selectedIndices) : [i];
                                                    
                                                    const moveHandler = (moveEvent: MouseEvent) => {
                                                        const dx = moveEvent.clientX - startX;
                                                        const dy = moveEvent.clientY - startY;
                                                        const distance = Math.sqrt(dx * dx + dy * dy);
                                                        
                                                        if (!isDragging && distance > 5) {
                                                            isDragging = true;
                                                            if (!historyPushed) {
                                                                pushHistory();
                                                                historyPushed = true;
                                                            }
                                                            setDraggingIndex(i);
                                                            if (!isMultiSelected) {
                                                                if (selectedObjectIndex !== i) {
                                                                    setSelectedObjectIndex(i);
                                                                    setSelectedArrowIndex(null);
                                                                }
                                                                setSelectedIndices(new Set());
                                                            }
                                                        }
                                                        
                                                        if (isDragging) {
                                                            const moveDx = moveEvent.movementX / zoom;
                                                            const moveDy = moveEvent.movementY / zoom;
                                                            setObjects(last => {
                                                                const next = [...last];
                                                                indicesToMove.forEach(idx => {
                                                                    next[idx] = {
                                                                        ...next[idx],
                                                                        position: {
                                                                            x: next[idx].position.x + moveDx,
                                                                            y: next[idx].position.y + moveDy
                                                                        }
                                                                    };
                                                                });
                                                                objectsRef.current = next;
                                                                return next;
                                                            });
                                                        }
                                                    };
                                                    
                                                    const upHandler = () => {
                                                        window.removeEventListener('mousemove', moveHandler);
                                                        window.removeEventListener('mouseup', upHandler);
                                                        if (isDragging) {
                                                            setDraggingIndex(null);
                                                            debouncedSave(objectsRef.current, arrowsRef.current, nameRef.current);
                                                        } else {
                                                            if (audioEl) {
                                                                if (audioEl.paused) {
                                                                    audioEl.play();
                                                                } else {
                                                                    audioEl.pause();
                                                                }
                                                            }
                                                        }
                                                    };
                                                    
                                                    window.addEventListener('mousemove', moveHandler);
                                                    window.addEventListener('mouseup', upHandler);
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            ) : it.style === 'folder' ? (
                                <div className="MindmapEditor_folder_block_content">
                                    <img 
                                        src={getFolderIconByIndex(0)} 
                                        alt="" 
                                        className="MindmapEditor_folder_block_icon"
                                    />
                                    <span className="MindmapEditor_folder_block_name">{it.folderName || it.text}</span>
                                </div>
                            ) : (
                            <div className={`MindmapEditor_object_content ${it.style === 'star' ? 'MindmapEditor_object_content--star' : ''}`}>
                                {it.taskId && (
                                    <span 
                                        className={`MindmapEditor_object_task_checkbox ${it.taskChecked ? 'MindmapEditor_object_task_checkbox--checked' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const task = allTasks.find(t => t.id === it.taskId);
                                            if (task) toggleTaskChecked(task);
                                        }}
                                    ></span>
                                )}
                                <div 
                                    ref={el => { textareaRefs.current[i] = el; }}
                                    className={`MindmapEditor_object_text ${draggingIndex === i ? 'MindmapEditor_object_text--dragging' : ''} ${editingIndex !== i ? 'MindmapEditor_object_text--inactive' : ''}`}
                                    style={it.textColor ? { color: it.textColor } : undefined}
                                    contentEditable={editingIndex === i}
                                    suppressContentEditableWarning={true}
                                    data-placeholder={!it.text && it.style === 'text' ? (translate('mindmap_text_placeholder') || 'Enter text...') : undefined}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.altKey) {
                                            e.preventDefault();
                                            const selection = document.getSelection();
                                            if (selection && selection.rangeCount > 0) {
                                                const range = selection.getRangeAt(0);
                                                range.deleteContents();
                                                const br = document.createElement('br');
                                                const zws = document.createTextNode('\u200B');
                                                range.insertNode(zws);
                                                range.insertNode(br);
                                                range.setStartAfter(zws);
                                                range.setEndAfter(zws);
                                                selection.removeAllRanges();
                                                selection.addRange(range);
                                                const target = e.target as HTMLDivElement;
                                                target.dispatchEvent(new Event('input', { bubbles: true }));
                                            }
                                        }
                                    }}
                                    onInput={(e) => {
                                        const target = e.target as HTMLDivElement;
                                        let text = target.innerText.replace(/\u00A0/g, ' ').replace(/\u200B/g, '');
                                        if (!text.trim()) {
                                            target.innerHTML = '';
                                            text = '';
                                        }
                                        const newObjects = [...objects];
                                        newObjects[i] = { ...newObjects[i], text };
                                        
                                        if (it.style === 'sticker') {
                                            const minHeight = 100;
                                            const newHeight = Math.max(minHeight, target.scrollHeight);
                                            const heightDelta = newHeight - newObjects[i].height;
                                            newObjects[i] = { 
                                                ...newObjects[i], 
                                                height: newHeight, 
                                                width: 150,
                                                position: {
                                                    x: newObjects[i].position.x,
                                                    y: newObjects[i].position.y + heightDelta / 2
                                                }
                                            };
                                        }
                                        
                                        setObjects(newObjects);
                                        objectsRef.current = newObjects;
                                        debouncedSave(newObjects, arrowsRef.current, name);
                                    }}
                                    onBlur={() => {
                                        if (editingIndex === i) {
                                            setEditingIndex(null);
                                            const obj = objects[i];
                                            if (obj.noteId) {
                                                syncNoteText(obj.noteId, obj.text);
                                            } else if (obj.taskId) {
                                                syncTaskText(obj.taskId, obj.text);
                                            }
                                        }
                                    }}
                                ></div>
                            </div>
                            )}
                            {selectedObjectIndex === i && (
                            <div className="MindmapEditor_object_markers">
                                <div className="MindmapEditor_object_marker MindmapEditor_object_marker_rt"
                                    onMouseDown={(downEvent) => {
                                        const ang = it.angle / 180 * Math.PI;
                                        const originalAspect = it.width / it.height;
                                        handleMarkerSize(downEvent, (last, moveEvent) => {
                                            const next = [...last];
                                            let mx = (moveEvent.movementX / zoom) * Math.cos(ang) + (moveEvent.movementY / zoom) * Math.sin(ang);
                                            let my = (moveEvent.movementY / zoom) * Math.cos(ang) - (moveEvent.movementX / zoom) * Math.sin(ang);
                                            let newWidth = next[i].width + mx;
                                            let newHeight = next[i].height - my;
                                            if (moveEvent.shiftKey) {
                                                if (Math.abs(mx) > Math.abs(my)) {
                                                    newWidth = next[i].width + mx;
                                                    newHeight = newWidth / originalAspect;
                                                } else {
                                                    newHeight = next[i].height - my;
                                                    newWidth = newHeight * originalAspect;
                                                }
                                            }
                                            const widthDelta = newWidth - next[i].width;
                                            const heightDelta = newHeight - next[i].height;
                                            next[i] = {
                                                ...next[i],
                                                width: newWidth,
                                                height: newHeight,
                                                position: {
                                                    x: next[i].position.x + (heightDelta * Math.sin(ang) / 2 + widthDelta * Math.cos(ang) / 2),
                                                    y: next[i].position.y + (-heightDelta * Math.cos(ang) / 2 + widthDelta * Math.sin(ang) / 2)
                                                }
                                            };
                                            return next;
                                        });
                                    }}
                                ></div>
                                <div className="MindmapEditor_object_marker MindmapEditor_object_marker_rb"
                                    onMouseDown={(downEvent) => {
                                        const ang = it.angle / 180 * Math.PI;
                                        const originalAspect = it.width / it.height;
                                        handleMarkerSize(downEvent, (last, moveEvent) => {
                                            const next = [...last];
                                            let mx = (moveEvent.movementX / zoom) * Math.cos(ang) + (moveEvent.movementY / zoom) * Math.sin(ang);
                                            let my = (moveEvent.movementY / zoom) * Math.cos(ang) - (moveEvent.movementX / zoom) * Math.sin(ang);
                                            let newWidth = next[i].width + mx;
                                            let newHeight = next[i].height + my;
                                            if (moveEvent.shiftKey) {
                                                if (Math.abs(mx) > Math.abs(my)) {
                                                    newWidth = next[i].width + mx;
                                                    newHeight = newWidth / originalAspect;
                                                } else {
                                                    newHeight = next[i].height + my;
                                                    newWidth = newHeight * originalAspect;
                                                }
                                            }
                                            const widthDelta = newWidth - next[i].width;
                                            const heightDelta = newHeight - next[i].height;
                                            next[i] = {
                                                ...next[i],
                                                width: newWidth,
                                                height: newHeight,
                                                position: {
                                                    x: next[i].position.x + (widthDelta * Math.cos(ang) / 2 - heightDelta * Math.sin(ang) / 2),
                                                    y: next[i].position.y + (widthDelta * Math.sin(ang) / 2 + heightDelta * Math.cos(ang) / 2)
                                                }
                                            };
                                            return next;
                                        });
                                    }}
                                ></div>
                                <div className="MindmapEditor_object_marker MindmapEditor_object_marker_lb"
                                    onMouseDown={(downEvent) => {
                                        const ang = it.angle / 180 * Math.PI;
                                        const originalAspect = it.width / it.height;
                                        handleMarkerSize(downEvent, (last, moveEvent) => {
                                            const next = [...last];
                                            let mx = (moveEvent.movementX / zoom) * Math.cos(ang) + (moveEvent.movementY / zoom) * Math.sin(ang);
                                            let my = (moveEvent.movementY / zoom) * Math.cos(ang) - (moveEvent.movementX / zoom) * Math.sin(ang);
                                            let newWidth = next[i].width - mx;
                                            let newHeight = next[i].height + my;
                                            if (moveEvent.shiftKey) {
                                                if (Math.abs(mx) > Math.abs(my)) {
                                                    newWidth = next[i].width - mx;
                                                    newHeight = newWidth / originalAspect;
                                                } else {
                                                    newHeight = next[i].height + my;
                                                    newWidth = newHeight * originalAspect;
                                                }
                                            }
                                            const widthDelta = newWidth - next[i].width;
                                            const heightDelta = newHeight - next[i].height;
                                            next[i] = {
                                                ...next[i],
                                                width: newWidth,
                                                height: newHeight,
                                                position: {
                                                    x: next[i].position.x + (-widthDelta * Math.cos(ang) / 2 - heightDelta * Math.sin(ang) / 2),
                                                    y: next[i].position.y + (-widthDelta * Math.sin(ang) / 2 + heightDelta * Math.cos(ang) / 2)
                                                }
                                            };
                                            return next;
                                        });
                                    }}
                                ></div>
                                <div className="MindmapEditor_object_marker MindmapEditor_object_marker_lt"
                                    onMouseDown={(downEvent) => {
                                        const ang = it.angle / 180 * Math.PI;
                                        const originalAspect = it.width / it.height;
                                        handleMarkerSize(downEvent, (last, moveEvent) => {
                                            const next = [...last];
                                            let mx = (moveEvent.movementX / zoom) * Math.cos(ang) + (moveEvent.movementY / zoom) * Math.sin(ang);
                                            let my = (moveEvent.movementY / zoom) * Math.cos(ang) - (moveEvent.movementX / zoom) * Math.sin(ang);
                                            let newWidth = next[i].width - mx;
                                            let newHeight = next[i].height - my;
                                            if (moveEvent.shiftKey) {
                                                if (Math.abs(mx) > Math.abs(my)) {
                                                    newWidth = next[i].width - mx;
                                                    newHeight = newWidth / originalAspect;
                                                } else {
                                                    newHeight = next[i].height - my;
                                                    newWidth = newHeight * originalAspect;
                                                }
                                            }
                                            const widthDelta = newWidth - next[i].width;
                                            const heightDelta = newHeight - next[i].height;
                                            next[i] = {
                                                ...next[i],
                                                width: newWidth,
                                                height: newHeight,
                                                position: {
                                                    x: next[i].position.x + (-widthDelta * Math.cos(ang) / 2 + heightDelta * Math.sin(ang) / 2),
                                                    y: next[i].position.y + (-widthDelta * Math.sin(ang) / 2 - heightDelta * Math.cos(ang) / 2)
                                                }
                                            };
                                            return next;
                                        });
                                    }}
                                ></div>
                                <div className="MindmapEditor_object_marker MindmapEditor_object_marker_rotate"
                                    onMouseDown={(downEvent) => {
                                        const ang = it.angle / 180 * Math.PI;
                                        const startPoint = {
                                            x: (it.position.x + it.width / 2) + Math.sin(ang) * (it.height / 2),
                                            y: (it.position.y + it.height / 2) - Math.cos(ang) * (it.height / 2)
                                        };
                                        const centerPoint = {
                                            x: it.position.x + it.width / 2,
                                            y: it.position.y + it.height / 2
                                        };
                                        handleMarkerSize(downEvent, (last, moveEvent) => {
                                            startPoint.x += moveEvent.movementX;
                                            startPoint.y += moveEvent.movementY;
                                            const next = [...last];
                                            let newAngle = Math.atan2(startPoint.x - centerPoint.x, -(startPoint.y - centerPoint.y)) / Math.PI * 180;
                                            if (moveEvent.shiftKey) {
                                                newAngle = Math.round(newAngle / 45) * 45;
                                            }
                                            next[i] = {
                                                ...next[i],
                                                angle: newAngle
                                            };
                                            return next;
                                        });
                                    }}
                                ></div>
                            </div>
                            )}
                            {arrowMode !== null || editingArrowEnd ? (
                                <div className="MindmapEditor_snap_points">
                                    {(['top', 'bottom', 'left', 'right', 'tl', 'tr', 'bl', 'br'] as SnapPointId[]).map(snapId => {
                                        const snapPos = {
                                            top: { left: '50%', top: 0, transform: 'translate(-50%, -50%)' },
                                            bottom: { left: '50%', bottom: 0, transform: 'translate(-50%, 50%)' },
                                            left: { left: 0, top: '50%', transform: 'translate(-50%, -50%)' },
                                            right: { right: 0, top: '50%', transform: 'translate(50%, -50%)' },
                                            tl: { left: 0, top: 0, transform: 'translate(-50%, -50%)' },
                                            tr: { right: 0, top: 0, transform: 'translate(50%, -50%)' },
                                            bl: { left: 0, bottom: 0, transform: 'translate(-50%, 50%)' },
                                            br: { right: 0, bottom: 0, transform: 'translate(50%, 50%)' }
                                        }[snapId];
                                        
                                        const isHoveredShape = hoveredObject === i;
                                        
                                        return (
                                            <div 
                                                key={snapId}
                                                className={`MindmapEditor_snap_point ${arrowStart?.objectIndex === i && arrowStart?.snapPoint === snapId ? 'MindmapEditor_snap_point--active' : ''} ${editingArrowEnd ? 'MindmapEditor_snap_point--editing' : ''} ${!isHoveredShape && !editingArrowEnd ? 'MindmapEditor_snap_point--dim' : ''}`}
                                                style={snapPos as React.CSSProperties}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    
                                                    if (editingArrowEnd && selectedArrowIndex !== null) {
                                                        const arrow = arrows[selectedArrowIndex];
                                                        const newArrows = [...arrows];
                                                        if (editingArrowEnd === 'start') {
                                                            newArrows[selectedArrowIndex] = {
                                                                ...arrow,
                                                                fromObjectIndex: i,
                                                                fromSnapPoint: snapId
                                                            };
                                                        } else {
                                                            newArrows[selectedArrowIndex] = {
                                                                ...arrow,
                                                                toObjectIndex: i,
                                                                toSnapPoint: snapId
                                                            };
                                                        }
                                                        updateArrows(newArrows);
                                                        setEditingArrowEnd(null);
                                                        setMousePos(null);
                                                        return;
                                                    }
                                                    
                                                    if (!arrowStart) {
                                                        setArrowStart({ objectIndex: i, snapPoint: snapId });
                                                    } else if (arrowStart.objectIndex !== i) {
                                                        const isManual = arrowMode && arrowMode.startsWith('manual-');
                                                        let manualPath: { x: number; y: number }[] | undefined;
                                                        
                                                        if (isManual) {
                                                            const fromObj = objects[arrowStart.objectIndex];
                                                            const toObj = objects[i];
                                                            const startPt = getSnapPointCoords(fromObj, arrowStart.snapPoint);
                                                            const endPt = getSnapPointCoords(toObj, snapId);
                                                            manualPath = calculateAutoPathWithSnaps(
                                                                startPt, endPt, 
                                                                arrowStart.snapPoint, snapId, 
                                                                objects, arrowStart.objectIndex, i
                                                            ) || calculateAutoPath(startPt, endPt);
                                                        }
                                                        
                                                        const newArrow: IMindmapArrow = {
                                                            id: generateArrowId(),
                                                            style: arrowMode!,
                                                            fromObjectIndex: arrowStart.objectIndex,
                                                            toObjectIndex: i,
                                                            fromSnapPoint: arrowStart.snapPoint,
                                                            toSnapPoint: snapId,
                                                            ...(manualPath && { manualPath })
                                                        };
                                                        
                                                        updateArrows([...arrows, newArrow]);
                                                        setArrowStart(null);
                                                        setMousePos(null);
                                                        setArrowMode(null);
                                                    }
                                                }}
                                            />
                                        );
                                    })}
                                </div>
                            ) : null}
                        </div>
                    })}
                    </div>
                    {selectionBox && (
                        <div 
                            className="MindmapEditor_selection_box"
                            style={{
                                left: Math.min(selectionBox.startX, selectionBox.currentX) * zoom + cameraOffset.x,
                                top: Math.min(selectionBox.startY, selectionBox.currentY) * zoom + cameraOffset.y,
                                width: Math.abs(selectionBox.currentX - selectionBox.startX) * zoom,
                                height: Math.abs(selectionBox.currentY - selectionBox.startY) * zoom
                            }}
                        />
                    )}
                    {selectedObjectIndex !== null && objects[selectedObjectIndex] && objects[selectedObjectIndex].style !== 'folder' && objects[selectedObjectIndex].style !== 'media' && (
                        <div 
                            className="MindmapEditor_style_popup"
                            style={{
                                left: (objects[selectedObjectIndex].position.x) * zoom + cameraOffset.x,
                                top: (objects[selectedObjectIndex].position.y + objects[selectedObjectIndex].height / 2) * zoom + cameraOffset.y + 10
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            {objects[selectedObjectIndex].style === 'drawing' ? (
                                <>
                                    <div className="MindmapEditor_style_popup_section">
                                        <span className="MindmapEditor_style_popup_label">{translate('mindmap_draw_color') || 'Color'}</span>
                                        <div className="MindmapEditor_style_popup_colors">
                                            {['#333333', '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#f90', '#a29bfe', '#fd79a8'].map(color => (
                                                <div 
                                                    key={color}
                                                    className={`MindmapEditor_style_popup_color ${objects[selectedObjectIndex].strokeColor === color ? 'MindmapEditor_style_popup_color--active' : ''}`}
                                                    style={{ backgroundColor: color }}
                                                    onClick={() => {
                                                        pushHistory();
                                                        const newObjects = [...objects];
                                                        newObjects[selectedObjectIndex] = { ...newObjects[selectedObjectIndex], strokeColor: color };
                                                        setObjects(newObjects);
                                                        objectsRef.current = newObjects;
                                                        debouncedSave(newObjects, arrowsRef.current, nameRef.current);
                                                    }}
                                                />
                                            ))}
                                            <input 
                                                type="color" 
                                                className="MindmapEditor_style_popup_picker"
                                                value={objects[selectedObjectIndex].strokeColor || '#333333'}
                                                onChange={(e) => {
                                                    pushHistory();
                                                    const newObjects = [...objects];
                                                    newObjects[selectedObjectIndex] = { ...newObjects[selectedObjectIndex], strokeColor: e.target.value };
                                                    setObjects(newObjects);
                                                    objectsRef.current = newObjects;
                                                    debouncedSave(newObjects, arrowsRef.current, nameRef.current);
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="MindmapEditor_style_popup_section">
                                        <span className="MindmapEditor_style_popup_label">{translate('mindmap_draw_width') || 'Width'}</span>
                                        <div className="MindmapEditor_style_popup_border">
                                            <select 
                                                className="MindmapEditor_style_popup_select"
                                                value={objects[selectedObjectIndex].strokeWidth ?? 3}
                                                onChange={(e) => {
                                                    pushHistory();
                                                    const newObjects = [...objects];
                                                    newObjects[selectedObjectIndex] = { ...newObjects[selectedObjectIndex], strokeWidth: parseInt(e.target.value) };
                                                    setObjects(newObjects);
                                                    objectsRef.current = newObjects;
                                                    debouncedSave(newObjects, arrowsRef.current, nameRef.current);
                                                }}
                                            >
                                                <option value="2">2px</option>
                                                <option value="3">3px</option>
                                                <option value="5">5px</option>
                                                <option value="8">8px</option>
                                            </select>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="MindmapEditor_style_popup_section">
                                        <span className="MindmapEditor_style_popup_label">{translate('mindmap_fill') || 'Fill'}</span>
                                        <div className="MindmapEditor_style_popup_colors">
                                            {['#ffcc00', '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7', '#dfe6e9', '#a29bfe'].map(color => (
                                                <div 
                                                    key={color}
                                                    className={`MindmapEditor_style_popup_color ${objects[selectedObjectIndex].fillColor === color ? 'MindmapEditor_style_popup_color--active' : ''}`}
                                                    style={{ backgroundColor: color }}
                                                    onClick={() => {
                                                        pushHistory();
                                                        const newObjects = [...objects];
                                                        newObjects[selectedObjectIndex] = { ...newObjects[selectedObjectIndex], fillColor: color };
                                                        setObjects(newObjects);
                                                        objectsRef.current = newObjects;
                                                        debouncedSave(newObjects, arrowsRef.current, nameRef.current);
                                                    }}
                                                />
                                            ))}
                                            <input 
                                                type="color" 
                                                className="MindmapEditor_style_popup_picker"
                                                value={objects[selectedObjectIndex].fillColor || '#ffcc00'}
                                                onChange={(e) => {
                                                    pushHistory();
                                                    const newObjects = [...objects];
                                                    newObjects[selectedObjectIndex] = { ...newObjects[selectedObjectIndex], fillColor: e.target.value };
                                                    setObjects(newObjects);
                                                    objectsRef.current = newObjects;
                                                    debouncedSave(newObjects, arrowsRef.current, nameRef.current);
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="MindmapEditor_style_popup_section">
                                        <span className="MindmapEditor_style_popup_label">{translate('mindmap_text_color') || 'Text color'}</span>
                                        <input 
                                            type="color" 
                                            className="MindmapEditor_style_popup_picker"
                                            value={objects[selectedObjectIndex].textColor || '#000000'}
                                            onChange={(e) => {
                                                pushHistory();
                                                const newObjects = [...objects];
                                                newObjects[selectedObjectIndex] = { ...newObjects[selectedObjectIndex], textColor: e.target.value };
                                                setObjects(newObjects);
                                                objectsRef.current = newObjects;
                                                debouncedSave(newObjects, arrowsRef.current, nameRef.current);
                                            }}
                                        />
                                    </div>
                                    <div className="MindmapEditor_style_popup_section">
                                        <span className="MindmapEditor_style_popup_label">{translate('mindmap_border') || 'Border'}</span>
                                        <div className="MindmapEditor_style_popup_border">
                                            <select 
                                                className="MindmapEditor_style_popup_select"
                                                value={objects[selectedObjectIndex].borderWidth ?? 0}
                                                onChange={(e) => {
                                                    pushHistory();
                                                    const newObjects = [...objects];
                                                    newObjects[selectedObjectIndex] = { ...newObjects[selectedObjectIndex], borderWidth: parseInt(e.target.value) };
                                                    setObjects(newObjects);
                                                    objectsRef.current = newObjects;
                                                    debouncedSave(newObjects, arrowsRef.current, nameRef.current);
                                                }}
                                            >
                                                <option value="0">0px</option>
                                                <option value="1">1px</option>
                                                <option value="2">2px</option>
                                                <option value="3">3px</option>
                                                <option value="4">4px</option>
                                                <option value="5">5px</option>
                                            </select>
                                            <input 
                                                type="color" 
                                                className="MindmapEditor_style_popup_picker"
                                                value={objects[selectedObjectIndex].borderColor || '#ff9900'}
                                                onChange={(e) => {
                                                    pushHistory();
                                                    const newObjects = [...objects];
                                                    newObjects[selectedObjectIndex] = { ...newObjects[selectedObjectIndex], borderColor: e.target.value };
                                                    setObjects(newObjects);
                                                    objectsRef.current = newObjects;
                                                    debouncedSave(newObjects, arrowsRef.current, nameRef.current);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                    <div className="MindmapEditor_zoom_controls">
                        <button 
                            className="MindmapEditor_zoom_btn"
                            onClick={undo}
                            disabled={historyStackRef.current.length === 0}
                            title={translate('mindmap_undo') || 'Undo (Ctrl+Z)'}
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M3 8h8a3 3 0 0 1 3 3v1M3 8l3-3M3 8l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                        <button 
                            className="MindmapEditor_zoom_btn"
                            onClick={centerCamera}
                            title={translate('mindmap_center_camera') || 'Center all'}
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16">
                                <rect x="2" y="2" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.5" rx="1"/>
                                <circle cx="8" cy="8" r="2" fill="currentColor"/>
                            </svg>
                        </button>
                        <div className="MindmapEditor_zoom_indicator">
                            {Math.round(zoom * 100)}%
                        </div>
                        <button 
                            className="MindmapEditor_zoom_btn"
                            onClick={() => setZoom(1)}
                            title={translate('mindmap_reset_zoom') || 'Reset zoom'}
                        >
                            1:1
                        </button>
                        <button 
                            className="MindmapEditor_zoom_btn MindmapEditor_fullscreen_btn"
                            onClick={() => setIsFullscreen(prev => !prev)}
                            title={isFullscreen ? (translate('mindmap_exit_fullscreen') || 'Exit fullscreen') : (translate('mindmap_fullscreen') || 'Fullscreen')}
                        >
                            {isFullscreen ? (
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M5 2v3H2M11 2v3h3M5 14v-3H2M11 14v-3h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            ) : (
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M2 5V2h3M14 5V2h-3M2 11v3h3M14 11v3h-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            )}
                        </button>
                    </div>
                    {contextMenu && (
                        <div 
                            className="MindmapEditor_contextMenu"
                            style={{ top: contextMenu.y, left: contextMenu.x }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div 
                                className="MindmapEditor_contextMenu_item"
                                onClick={() => handleDeleteBlock(contextMenu.index)}
                            >
                                {translate('mindmap_delete_block') || 'Delete'}
                            </div>
                        </div>
                    )}
                    {selectedArrowIndex !== null && arrows[selectedArrowIndex]?.style?.startsWith('manual-') && (
                        <div className="MindmapEditor_manual_hint">
                            <div>{translate('mindmap_manual_hint_line1')}</div>
                            <div>{translate('mindmap_manual_hint_line2')}</div>
                        </div>
                    )}
                </div>
            </div>
        </div>
        {showDeletePopup && (
            <ConfirmationPopup
                message={translate('mindmap_delete_confirm') || 'Delete this board?'}
                confirmText={translate('mindmap_delete') || 'Delete'}
                cancelText={translate('mindmap_delete_cancel') || 'Cancel'}
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                confirmButtonStyle="danger"
            />
        )}
        {showMediaModal && (
            <div className="MindmapEditor_media_modal_overlay" onClick={() => setShowMediaModal(false)}>
                <div className="MindmapEditor_media_modal" onClick={(e) => e.stopPropagation()}>
                    <div className="MindmapEditor_media_modal_header">
                        <h3>{translate('mindmap_add_media') || 'Add Media'}</h3>
                        <button className="MindmapEditor_media_modal_close" onClick={() => setShowMediaModal(false)}>×</button>
                    </div>
                    <div className="MindmapEditor_media_modal_content">
                        <div className="MindmapEditor_media_modal_section">
                            <label>{translate('mindmap_media_url') || 'URL'}</label>
                            <div className="MindmapEditor_media_modal_url_row">
                                <input 
                                    type="text" 
                                    placeholder={translate('mindmap_media_url_placeholder') || 'https://example.com/image.jpg'}
                                    id="mediaUrlInput"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleMediaUrlSubmit((e.target as HTMLInputElement).value);
                                        }
                                    }}
                                />
                                <button onClick={() => {
                                    const input = document.getElementById('mediaUrlInput') as HTMLInputElement;
                                    handleMediaUrlSubmit(input?.value || '');
                                }}>{translate('mindmap_add') || 'Add'}</button>
                            </div>
                        </div>
                        <div className="MindmapEditor_media_modal_divider">
                            <span>{translate('mindmap_or') || 'or'}</span>
                        </div>
                        <div className="MindmapEditor_media_modal_section">
                            <label>{translate('mindmap_upload_file') || 'Upload File'}</label>
                            <input 
                                type="file" 
                                accept="image/*,video/*,audio/*"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleMediaFileUpload(file);
                                }}
                            />
                            {mediaUploadProgress !== null && (
                                <div className="MindmapEditor_media_modal_progress">
                                    <div 
                                        className="MindmapEditor_media_modal_progress_bar"
                                        style={{ width: `${mediaUploadProgress}%` }}
                                    />
                                    <span>{mediaUploadProgress}%</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )}
    </div>
}
